import {
  Component, OnInit, OnDestroy, AfterViewInit,
  ViewChild, ElementRef, HostListener, NgZone
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil, take, withLatestFrom } from 'rxjs/operators';

import { AppState } from '@store/app.state';
import { FrameActions } from '@store/actions/frame.actions';
import { SettingsActions } from '@store/actions/settings.actions';
import { ToolActions } from '@store/actions/tool.actions';
import { selectAllFrames, selectCurrentFrame, selectCurrentFrameId, selectActiveCelPixels } from '@store/selectors/frame.selectors';
import { selectActiveTool, selectActiveColor, selectBrushSize, selectSymmetryMode, selectShapeFillMode } from '@store/selectors/tool.selectors';
import { selectSettings } from '@store/selectors/settings.selector';
import { PixelFrame, createBlankFrame } from '@models/frame.model';
import { Settings } from '@models/settings.model';
import { ToolType, SymmetryMode, ShapeFillMode } from '@models/tool.model';
import { compositeLayers } from '@models/layer.model';
import { CollaborationService, PeerState } from '@app/services/collaboration.service';
import { PxlNumberStepperDirective } from '@app/shared/pxl-number-stepper/pxl-number-stepper.directive';
import { UserConfigActions } from '@store/actions/user-config.actions';
import { selectCanvasHintsExpanded, selectUserConfig } from '@store/selectors/user-config.selectors';
import { LucideChevronLeft, LucideChevronRight } from '@lucide/angular';

interface Pt { x: number; y: number; }
interface SelectionRect { r1: number; c1: number; r2: number; c2: number; }
interface FloatingClipboard {
  pixels: string[][];
  x: number; y: number;  // current top-left position in grid coords
  isDragging: boolean;
  dragOffsetX: number; dragOffsetY: number;
  // Set when the clip was created by a shape tool - enables clean redraw on resize
  shapeType?: ToolType;
  shapeFillMode?: ShapeFillMode;
  shapeColor?: string;
}
interface ImportConfig {
  imgEl: HTMLImageElement;
  x: number; y: number;
  scaleW: number; scaleH: number;
  opacity: number;
}

@Component({
  selector: 'app-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, PxlNumberStepperDirective, LucideChevronLeft, LucideChevronRight],
  templateUrl: './editor.html',
  styleUrls: ['./editor.css'],
})
export class Editor implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  private destroy$ = new Subject<void>();
  private frame: PixelFrame | null = null;
  private allFrames: PixelFrame[] = [];
  private frameId: string | null = null;
  tool: ToolType = 'draw';
  private color = '#00ff9d';
  private settings: Settings | null = null;
  private brushSize = 1;
  private symmetryMode: SymmetryMode = 'none';
  shapeFillMode: ShapeFillMode = 'outline';

  zoom = 1;
  private ox = 0;
  private oy = 0;
  private centered = false;

  // Pan state
  private isMidPanning = false;
  private panStartX = 0; private panStartY = 0;
  isSpacePanning = false;          // space key held → grab cursor + pan on mousemove
  private isDrawing = false;
  private didDraw = false;  // tracks if pixels were painted this stroke
  isPanning = false;
  private lastPaintCell: {x:number;y:number} | null = null;

  // ── Selection ──
  selection: SelectionRect | null = null;
  private selStart: Pt | null = null;
  private isSelecting = false;
  floatingClip: FloatingClipboard | null = null;  // floating paste layer

  // ── Move tool ──
  private moveStart: Pt | null = null;     // grid-coord where drag began
  private moveSelOrigin: SelectionRect | null = null;
  isMoveActive = false;

  // ── Shape drawing ──
  private shapeStart: Pt | null = null;
  private shapeCurrent: Pt | null = null;
  private isShaping = false;
  shapeDimsVisible = false;
  // Exact-size shape input
  exactW = 0; exactH = 0; exactR = 0;
  // Selection transform state
  selResizeHandle: 'nw'|'n'|'ne'|'e'|'se'|'s'|'sw'|'w'|'rot'|null = null;
  selResizeOrigin: SelectionRect | null = null;
  selRotAngle = 0;
  selRotCenter: Pt | null = null;
  selRotPixels: string[][] | null = null;
  isSelTransforming = false;
  private transformSourcePixels: string[][] | null = null;  // selected content at transform start
  private transformOriginSel: SelectionRect | null = null;  // selection bounds at transform start
  private transformPreviewPixels: string[][] | null = null;
  private transformPreviewRow = 0;
  private transformPreviewCol = 0;
  shapePreviewDims: {
    w?: number; h?: number; r?: number; rx?: number; ry?: number;
    base?: number; height?: number; length?: number;
  } = {};

  // ── Floating clip resize ──
  private isFcResizing = false;
  private fcResizeHandle: 'nw'|'n'|'ne'|'e'|'se'|'s'|'sw'|'w' | null = null;
  private fcResizeStartBounds: { r1: number; c1: number; r2: number; c2: number } | null = null;
  private fcResizeOrigPixels: string[][] | null = null;

  // ── Polygon tool ──
  polyPoints: Pt[] = [];

  // ── Image import ──
  showImportModal = false;
  importConfig: ImportConfig | null = null;
  importOpacity = 100;
  importX = 0; importY = 0;
  importScaleW = 0; importScaleH = 0;
  private importDragging = false;
  private importDragStart: Pt | null = null;

  private resizeObserver!: ResizeObserver;

  // ── Collab peer cursors ──
  private peers: PeerState[] = [];
  private _remoteOverlayPixels: string[][] | null = null;
  private _lastCursorCell: { x: number; y: number } | null = null; // throttle cursor broadcasts
  private _lastPointerClient: Pt | null = null;
  private hoverCell: Pt | null = null;
  collabSyncing = false;
  /** Mirrored from user config (IDB); shortcut bar expanded vs chevron-only. */
  canvasHintsExpanded = false;
  /** Active cel's pixel grid for the current frame×layer (undefined = empty cel). */
  private activeCelPixels: string[][] | undefined = undefined;

  constructor(private store: Store<AppState>, private zone: NgZone, private collabSvc: CollaborationService) {}

  ngOnInit(): void {
    this.store
      .select(selectCanvasHintsExpanded)
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => (this.canvasHintsExpanded = v));

    this.store
      .select(selectActiveCelPixels)
      .pipe(takeUntil(this.destroy$))
      .subscribe((pixels) => (this.activeCelPixels = pixels));

    combineLatest([
      this.store.select(selectCurrentFrame),
      this.store.select(selectActiveTool),
      this.store.select(selectActiveColor),
      this.store.select(selectSettings),
      this.store.select(selectCurrentFrameId),
      this.store.select(selectBrushSize),
      this.store.select(selectSymmetryMode),
      this.store.select(selectShapeFillMode),
      this.store.select(selectAllFrames),
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([frame, tool, color, settings, fid, brushSize, symmetry, shapeFill, allFrames]) => {
        const resChanged = settings.Resolution.width !== (this.settings?.Resolution.width ?? 0);
        this.frame = frame;
        this._remoteOverlayPixels = null; // store is now up to date, clear overlay

        // When the tool changes, clear any in-progress state from the previous tool
        // so only one tool is ever active at a time on the canvas.
        if (tool !== this.tool) {
          this.selection       = null;
          this.floatingClip    = null;
          this.polyPoints      = [];
          this.isShaping       = false;
          this.shapeStart      = null;
          this.shapeCurrent    = null;
          this.isSelecting     = false;
          this.isDrawing       = false;
          this.isMoveActive    = false;
          this.isSelTransforming = false;
          this.selResizeHandle = null;
          this.selRotCenter    = null;
          this.transformSourcePixels = null;
          this.transformOriginSel = null;
          this.isFcResizing    = false;
          this.fcResizeHandle  = null;
          this.fcResizeStartBounds = null;
          this.fcResizeOrigPixels  = null;
          this.transformPreviewPixels = null;
        }

        this.tool = tool;
        this.setCursor(this.getToolCursor());
        this.color = color;
        this.settings = settings;
        this.frameId = fid;
        if (fid) this.collabSvc.updateActiveFrame(fid);
        this.brushSize = brushSize;
        this.symmetryMode = symmetry;
        this.shapeFillMode = shapeFill;
        this.allFrames = allFrames;
        if (resChanged || !this.centered) this.centerCanvas();
        this.draw();
      });

    // Subscribe to peer awareness state for cursor rendering
    this.collabSvc.peers$.pipe(takeUntil(this.destroy$)).subscribe(peers => {
      this.peers = peers;
      this.draw();
    });
    // Syncing overlay should reflect actual hydration gate state.
    this.collabSvc.syncing$.pipe(takeUntil(this.destroy$)).subscribe(v => this.collabSyncing = v);

    // Remote layer structure changes from peers: update layers + activeLayerId in store.
    this.collabSvc.remoteLayerSync$.pipe(takeUntil(this.destroy$)).subscribe(({ layers, activeLayerId }) => {
      this.store.dispatch(FrameActions.syncRemoteLayers({ layers, activeLayerId }));
    });

    // Remote cel data: store per-layer pixel data so recompositing works correctly.
    // Without this, when a member draws they recomposite from empty local cels, wiping all other layers.
    this.collabSvc.remoteCelChange$.pipe(takeUntil(this.destroy$)).subscribe(({ cels }) => {
      this.store.dispatch(FrameActions.syncRemoteCels({ cels }));
    });

    // Remote cel clear: a peer's collab undo reverted a layer to blank.
    // Remove those cel keys locally and recomposite so the cleared layer disappears.
    this.collabSvc.remoteCelClear$.pipe(takeUntil(this.destroy$)).subscribe(({ celKeys }) => {
      this.store.dispatch(FrameActions.syncRemoteCelClear({ celKeys }));
    });

    // Remote pixel changes from peers: write directly to store and redraw.
    // syncRemoteFrame bypasses compositeLayers so no pixel corruption.
    this.collabSvc.remotePixelChange$.pipe(takeUntil(this.destroy$)).subscribe(({ frameId, pixels }) => {
      this.store.dispatch(FrameActions.syncRemoteFrame({ frameId, pixels }));
      if (frameId === this.frameId) {
        this._remoteOverlayPixels = pixels;
        this.draw();
      }
    });

    // Full sync on join: replace the local store completely with the room's authoritative state.
    // This fires once when a member first connects, so they see the admin's canvas even if
    // they had a completely different project open locally.
    this.collabSvc.remoteFullSync$
      .pipe(takeUntil(this.destroy$), withLatestFrom(this.store.select(selectAllFrames)))
      .subscribe(([{ frames, frameOrder }, existingFrames]) => {
        if (frames.length === 0) return;
        const existingMap = new Map(existingFrames.map(f => [f.id, f]));
        const orderedIds = frameOrder.length > 0 ? frameOrder : frames.map(f => f.frameId);
        const syncedFrames: PixelFrame[] = orderedIds
          .map(id => frames.find(f => f.frameId === id))
          .filter((f): f is { frameId: string; pixels: string[][] } => !!f)
          .map(f => {
            const existing = existingMap.get(f.frameId);
            if (existing) {
              // Reuse existing frame, just update composited pixel data
              return { ...existing, pixels: f.pixels };
            }
            // New frame — composited pixels only; layers/cels live in store separately
            return { id: f.frameId, pixels: f.pixels } as PixelFrame;
          });
        if (syncedFrames.length > 0) {
          // Use syncRemoteFullSync (not setFrames) so collabFrameStructure$ does NOT
          // fire and re-broadcast all 30 frames back to the room.  setFrames would
          // trigger broadcastAllFramesAtomic, creating a ~900 KB feedback-loop
          // transaction that overwhelms the WebRTC data channel.
          this.store.dispatch(FrameActions.syncRemoteFullSync({ frames: syncedFrames }));
          this.collabSvc.markCanvasHydrated();
          // Select the first frame from the room's authoritative order so all
          // users land on the same frame after joining
          const firstId = syncedFrames[0]?.id;
          if (firstId) this.store.dispatch(FrameActions.selectFrame({ frameId: firstId }));
          // Immediately update this.frame so draw calls before the store async
          // subscription fires use the correct synced frame (not the stale local one)
          const firstFrame = syncedFrames[0];
          if (firstFrame) {
            this.frame = firstFrame;
            this.frameId = firstFrame.id;
            this.allFrames = syncedFrames;
            // Also sync the local Resolution setting to the room's canvas size.
            const rh = firstFrame.pixels.length;
            const rw = firstFrame.pixels[0]?.length ?? 0;
            if (rw > 0 && rh > 0 && this.settings &&
                (this.settings.Resolution.width !== rw || this.settings.Resolution.height !== rh)) {
              const newResolution = {
                ...this.settings.Resolution,
                width: rw, height: rh,
                exportWidth: rw * 20, exportHeight: rh * 20
              };
              const newSettings = { ...this.settings, Resolution: newResolution };
              this.settings = newSettings;
              this.store.dispatch(SettingsActions.updateSettings({ settings: newSettings }));
            }
            this.draw();
          }
        }
      });

    // Remote frame delete - a peer removed a frame; delete it locally.
    // Uses syncRemoteFrameDelete (not deleteFrame) so collabFrameStructure$
    // does NOT re-broadcast the delete back to the room, preventing feedback loops.
    this.collabSvc.remoteFrameDelete$.pipe(takeUntil(this.destroy$)).subscribe(frameId => {
      this.store.dispatch(FrameActions.syncRemoteFrameDelete({ frameId }));
    });

    // Remote frame order - a peer added, reordered, or deleted a frame.
    // Uses syncRemoteFrameOrder (not setFrames) so undo history is preserved
    // and persistence is NOT triggered for intermediate collab states.
    this.collabSvc.remoteFrameOrder$.pipe(
      takeUntil(this.destroy$),
      withLatestFrom(this.store.select(selectAllFrames))
    ).subscribe(([remoteOrder, localFrames]) => {
      if (remoteOrder.length === 0) return;

      // Deduplicate: concurrent delete(0,n)+insert(0,ids) on the same YArray by
      // two peers produces duplicate entries after Yjs CRDT merge.  Without this
      // guard, two store PixelFrame objects end up sharing the same .id, so
      // deleting one frame (filter by id) silently deletes both.
      const seen = new Set<string>();
      const dedupedOrder = remoteOrder.filter(id => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      const localMap = new Map(localFrames.map(f => [f.id, f]));
      // Use settings resolution for blank placeholders - never inherit a stale frame size
      const w = this.settings?.Resolution.width  ?? 54;
      const h = this.settings?.Resolution.height ?? 96;
      const reordered: PixelFrame[] = dedupedOrder.map(id => {
        if (localMap.has(id)) return localMap.get(id)!;
        // New frame from remote: prefer direct snapshot from collab doc to avoid
        // transient/permanent blank placeholders when deep pixel events are missed.
        const snapshot = this.collabSvc.getFramePixelsSnapshot(id);
        if (snapshot) {
          const f = createBlankFrame(snapshot.width, snapshot.height, id);
          f.pixels = snapshot.pixels.map(row => [...row]);
          return f;
        }
        return createBlankFrame(w, h, id);
      });
      this.store.dispatch(FrameActions.syncRemoteFrameOrder({ frames: reordered }));
    });
  }

  ngAfterViewInit(): void {
    const container = this.canvasRef.nativeElement.parentElement!;
    this.resizeObserver = new ResizeObserver(() => {
      this.zone.run(() => { if (!this.centered) this.centerCanvas(); this.draw(); });
    });
    this.resizeObserver.observe(container);
    setTimeout(() => { this.centerCanvas(); this.draw(); }, 50);
  }

  ngOnDestroy(): void {
    this.destroy$.next(); this.destroy$.complete();
    this.resizeObserver?.disconnect();
  }

  toggleCanvasHints(): void {
    this.store
      .select(selectUserConfig)
      .pipe(take(1))
      .subscribe((cfg) => {
        const cur = cfg.canvasHintsExpanded === true;
        this.store.dispatch(UserConfigActions.setCanvasHintsExpanded({ expanded: !cur }));
      });
  }

  private getContainerSize() {
    const el = this.canvasRef?.nativeElement?.parentElement;
    return { w: el?.clientWidth || 800, h: el?.clientHeight || 600 };
  }

  centerCanvas(): void {
    if (!this.settings) return;
    const { w, h } = this.getContainerSize();
    if (w < 10 || h < 10) return;
    const cs = this.settings.cellSize ?? 8;
    const gw = this.settings.Resolution.width * cs;
    const gh = this.settings.Resolution.height * cs;
    this.zoom = Math.min((w * 0.88) / gw, (h * 0.88) / gh, 4);
    this.ox = (w - gw * this.zoom) / 2;
    this.oy = (h - gh * this.zoom) / 2;
    this.centered = true;
  }

  // ────────────────────────────────────────────────────────────────
  //  DRAW
  // ────────────────────────────────────────────────────────────────
  draw(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas || !this.settings) return;
    const ctx = canvas.getContext('2d')!;
    const { w: cw, h: ch } = this.getContainerSize();
    if (cw < 10 || ch < 10) return;
    canvas.width = cw; canvas.height = ch;
    ctx.imageSmoothingEnabled = false;

    const { width, height } = this.settings.Resolution;
    const cs = (this.settings.cellSize ?? 8) * this.zoom;
    const glw = this.settings.gridLineWidth ?? 1;

    // Background + checkerboard - theme-aware
    const canvasBg = getComputedStyle(document.body).getPropertyValue('--canvas-bg').trim() || '#0d0d18';
    ctx.fillStyle = canvasBg;
    ctx.fillRect(0, 0, cw, ch);
    const isLight = getComputedStyle(document.body).getPropertyValue('--text').trim().startsWith('#1') || getComputedStyle(document.body).getPropertyValue('--text').trim().startsWith('#0');
    ctx.fillStyle = isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.018)';
    const sz = 16;
    for (let y = 0; y < ch; y += sz*2)
      for (let x = 0; x < cw; x += sz*2) {
        ctx.fillRect(x, y, sz, sz);
        ctx.fillRect(x+sz, y+sz, sz, sz);
      }

    // ── Onion skinning ──
    if (this.settings.onionSkinEnabled && this.allFrames.length > 1) {
      const idx = this.allFrames.findIndex(f => f.id === this.frameId);
      const n = this.settings!.onionSkinFrames ?? 2;
      const op = (this.settings!.onionSkinOpacity ?? 20) / 100;
      for (let d = -n; d <= n; d++) {
        if (d === 0) continue;
        const fi = idx + d;
        if (fi < 0 || fi >= this.allFrames.length) continue;
        const pf = this.allFrames[fi];
        const tint = d < 0 ? `rgba(255,60,172,${op * (1-Math.abs(d)/n*0.5)})` : `rgba(0,255,157,${op * (1-Math.abs(d)/n*0.5)})`;
        ctx.save();
        ctx.globalAlpha = op * (1 - Math.abs(d)/n * 0.4);
        this.drawPixelGrid(ctx, pf.pixels, width, height, cs);
        // Tint overlay
        ctx.globalAlpha = op * 0.3;
        ctx.fillStyle = tint;
        ctx.fillRect(Math.round(this.ox), Math.round(this.oy), Math.round(width*cs), Math.round(height*cs));
        ctx.restore();
      }
    }

    // ── Image import ghost ──
    if (this.importConfig) {
      const ic = this.importConfig;
      ctx.save(); ctx.globalAlpha = ic.opacity/100;
      ctx.drawImage(ic.imgEl, Math.round(ic.x*cs+this.ox), Math.round(ic.y*cs+this.oy), Math.round(ic.scaleW*cs), Math.round(ic.scaleH*cs));
      ctx.globalAlpha = 0.8; ctx.strokeStyle = '#ff3cac'; ctx.lineWidth = 1.5;
      ctx.setLineDash([4,3]);
      ctx.strokeRect(Math.round(ic.x*cs+this.ox)+0.5, Math.round(ic.y*cs+this.oy)+0.5, Math.round(ic.scaleW*cs), Math.round(ic.scaleH*cs));
      ctx.setLineDash([]); ctx.restore();
    }

    // ── Main pixels ── use remote overlay if present (shows remote changes instantly)
    const displayPixels = this._remoteOverlayPixels ?? this.frame?.pixels ?? [];
    this.drawPixelGrid(ctx, displayPixels, width, height, cs);

    // ── Transform preview overlay (non-destructive while dragging) ──
    if (this.transformPreviewPixels && (this.isMoveActive || this.isSelTransforming)) {
      ctx.save();
      ctx.globalAlpha = 0.62;
      for (let r = 0; r < this.transformPreviewPixels.length; r++) {
        for (let c = 0; c < (this.transformPreviewPixels[r]?.length ?? 0); c++) {
          const px = this.transformPreviewPixels[r][c];
          if (!px || px === '#00000000' || px === 'transparent') continue;
          const nr = this.transformPreviewRow + r;
          const nc = this.transformPreviewCol + c;
          if (nr < 0 || nr >= height || nc < 0 || nc >= width) continue;
          ctx.fillStyle = px;
          ctx.fillRect(Math.round(nc * cs + this.ox), Math.round(nr * cs + this.oy), Math.ceil(cs), Math.ceil(cs));
        }
      }
      ctx.restore();
      // Keep the axis-aligned preview box for move/resize only.
      // During free-rotate, the rotated pink selection outline is drawn below.
      if (!(this.isSelTransforming && this.selRotCenter)) {
        const pw = this.transformPreviewPixels[0]?.length ?? 0;
        const ph = this.transformPreviewPixels.length;
        ctx.strokeStyle = '#ff3cac';
        ctx.lineWidth = 1.2;
        ctx.setLineDash([4, 3]);
        ctx.strokeRect(
          Math.round(this.transformPreviewCol * cs + this.ox) + 0.5,
          Math.round(this.transformPreviewRow * cs + this.oy) + 0.5,
          Math.round(pw * cs),
          Math.round(ph * cs)
        );
        ctx.setLineDash([]);
      }
    }

    // ── Hover brush preview (draw/erase) ──
    if (this.hoverCell && (this.tool === 'draw' || this.tool === 'erase') && !this.isReadOnlyInRoom()) {
      this.drawHoverBrushPreview(ctx, this.hoverCell, width, height, cs);
    }

    // ── Shape preview ──
    if (this.isShaping && this.shapeStart && this.shapeCurrent) {
      this.drawShapePreview(ctx, cs);
    }

    // ── Polygon in-progress preview ──
    if (this.tool === 'polygon' && this.polyPoints.length > 0) {
      ctx.save();
      ctx.strokeStyle = this.color;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(Math.round(this.polyPoints[0].x * cs + this.ox), Math.round(this.polyPoints[0].y * cs + this.oy));
      for (let i = 1; i < this.polyPoints.length; i++) {
        ctx.lineTo(Math.round(this.polyPoints[i].x * cs + this.ox), Math.round(this.polyPoints[i].y * cs + this.oy));
      }
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }

    // ── Floating clipboard ──
    if (this.floatingClip) {
      const fc = this.floatingClip;
      ctx.save(); ctx.globalAlpha = 0.85;
      for (let r = 0; r < fc.pixels.length; r++) {
        for (let c = 0; c < fc.pixels[r].length; c++) {
          const px = fc.pixels[r][c];
          if (!px || px === '#00000000' || px === 'transparent') continue;
          ctx.fillStyle = px;
          ctx.fillRect(Math.round((fc.x+c)*cs+this.ox), Math.round((fc.y+r)*cs+this.oy), Math.ceil(cs), Math.ceil(cs));
        }
      }
      ctx.restore();
      // Border
      const fw = fc.pixels[0]?.length ?? 0, fh = fc.pixels.length;
      ctx.strokeStyle = '#ff3cac'; ctx.lineWidth = 1.5; ctx.setLineDash([5,3]);
      ctx.strokeRect(Math.round(fc.x*cs+this.ox)+0.5, Math.round(fc.y*cs+this.oy)+0.5, Math.round(fw*cs), Math.round(fh*cs));
      ctx.setLineDash([]);
      // Size badge
      ctx.fillStyle = 'rgba(0,0,0,0.6)'; ctx.fillRect(Math.round(fc.x*cs+this.ox)+2, Math.round(fc.y*cs+this.oy)+2, 44, 14);
      ctx.fillStyle = '#ff3cac'; ctx.font = 'bold 9px monospace';
      ctx.fillText(`${fw}×${fh}`, Math.round(fc.x*cs+this.ox)+5, Math.round(fc.y*cs+this.oy)+12);
      // 8 resize handles
      const fcSx = Math.round(fc.x*cs+this.ox), fcSy = Math.round(fc.y*cs+this.oy);
      const fcSw = Math.round(fw*cs), fcSh = Math.round(fh*cs);
      const fcHpts: [number,number][] = [
        [fcSx,         fcSy        ],[fcSx+fcSw/2, fcSy        ],[fcSx+fcSw, fcSy        ],
        [fcSx+fcSw,    fcSy+fcSh/2 ],[fcSx+fcSw,  fcSy+fcSh   ],[fcSx+fcSw/2, fcSy+fcSh ],
        [fcSx,         fcSy+fcSh   ],[fcSx,        fcSy+fcSh/2 ],
      ];
      ctx.fillStyle = '#ff3cac';
      for (const [hx,hy] of fcHpts) { ctx.fillRect(hx-4, hy-4, 8, 8); }
    }

    // ── Grid lines ──
    if (glw > 0 && cs >= 3) {
      ctx.strokeStyle = `rgba(255,255,255,${Math.min(0.5, glw*0.12)})`;
      ctx.lineWidth = glw;
      ctx.beginPath();
      for (let x = 0; x <= width; x++) { const px = Math.round(x*cs+this.ox)+0.5; ctx.moveTo(px, Math.round(this.oy)); ctx.lineTo(px, Math.round(height*cs+this.oy)); }
      for (let y = 0; y <= height; y++) { const py = Math.round(y*cs+this.oy)+0.5; ctx.moveTo(Math.round(this.ox), py); ctx.lineTo(Math.round(width*cs+this.ox), py); }
      ctx.stroke();
    }

    // ── Canvas border ──
    const acc = this.settings.theme === 'light' ? '#0070f3' : '#00ff9d';
    ctx.strokeStyle = acc; ctx.globalAlpha = 0.45; ctx.lineWidth = 1.5; ctx.setLineDash([5,5]);
    ctx.strokeRect(Math.round(this.ox)+0.5, Math.round(this.oy)+0.5, Math.round(width*cs), Math.round(height*cs));
    ctx.setLineDash([]); ctx.globalAlpha = 1;

    // ── Symmetry guides ──
    if (this.symmetryMode !== 'none') {
      ctx.save();
      // Clip to canvas area only
      ctx.beginPath();
      ctx.rect(this.ox, this.oy, width*cs, height*cs);
      ctx.clip();
      ctx.strokeStyle = 'rgba(255,220,80,0.7)'; ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      const cw = width*cs, ch = height*cs;
      const mx = this.ox + cw/2, my = this.oy + ch/2;
      const l = this.ox, r = this.ox+cw, t = this.oy, b = this.oy+ch;
      const drawLine = (x1:number,y1:number,x2:number,y2:number) => {
        ctx.beginPath(); ctx.moveTo(Math.round(x1)+0.5, Math.round(y1)+0.5);
        ctx.lineTo(Math.round(x2)+0.5, Math.round(y2)+0.5); ctx.stroke();
      };
      const mode = this.symmetryMode;
      if (mode==='vertical'||mode==='quad') drawLine(mx,t,mx,b);
      if (mode==='horizontal'||mode==='quad') drawLine(l,my,r,my);
      if (mode==='diagonal-fwd') drawLine(l,b,r,t);
      if (mode==='diagonal-bk')  drawLine(l,t,r,b);
      const radialM = mode.match(/^radial-(\d+)$/);
      const starM   = mode.match(/^star-(\d+)$/);
      const n = radialM ? +radialM[1] : starM ? +starM[1] : 0;
      if (n > 0) {
        for (let i = 0; i < n; i++) {
          const angle = (Math.PI / n) * i;
          const cos = Math.cos(angle), sin = Math.sin(angle);
          const len = Math.max(cw,ch);
          drawLine(mx - cos*len, my - sin*len, mx + cos*len, my + sin*len);
        }
      }
      ctx.restore();
    }

    // ── Selection ──
    if (this.selection) {
      if (this.isSelTransforming && this.selRotCenter && this.transformOriginSel && this.transformSourcePixels) {
        const srcW = this.transformSourcePixels[0]?.length ?? 0;
        const srcH = this.transformSourcePixels.length;
        const cx = this.ox + (this.transformOriginSel.c1 + srcW / 2) * cs;
        const cy = this.oy + (this.transformOriginSel.r1 + srcH / 2) * cs;
        const hw = (srcW * cs) / 2;
        const hh = (srcH * cs) / 2;
        const rad = this.selRotAngle * Math.PI / 180;
        const cos = Math.cos(rad), sin = Math.sin(rad);
        const corners: [number, number][] = [
          [-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]
        ].map(([x, y]) => [cx + x * cos - y * sin, cy + x * sin + y * cos]) as [number, number][];

        ctx.fillStyle = 'rgba(255,60,172,0.05)';
        ctx.beginPath();
        ctx.moveTo(corners[0][0], corners[0][1]);
        ctx.lineTo(corners[1][0], corners[1][1]);
        ctx.lineTo(corners[2][0], corners[2][1]);
        ctx.lineTo(corners[3][0], corners[3][1]);
        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = '#ff3cac';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([5, 3]);
        ctx.beginPath();
        ctx.moveTo(corners[0][0], corners[0][1]);
        ctx.lineTo(corners[1][0], corners[1][1]);
        ctx.lineTo(corners[2][0], corners[2][1]);
        ctx.lineTo(corners[3][0], corners[3][1]);
        ctx.closePath();
        ctx.stroke();
        ctx.setLineDash([]);

        // Rotation angle badge near center
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.fillRect(cx - 18, cy - 7, 36, 14);
        ctx.fillStyle = '#ff3cac';
        ctx.font = 'bold 9px monospace';
        ctx.fillText(`${Math.round(this.selRotAngle)}°`, cx - 14, cy + 3);
      } else {
        const { r1, c1, r2, c2 } = this.selection;
        const sx = Math.round(Math.min(c1,c2)*cs+this.ox), sy = Math.round(Math.min(r1,r2)*cs+this.oy);
        const sw = Math.round((Math.abs(c2-c1)+1)*cs), sh = Math.round((Math.abs(r2-r1)+1)*cs);
        ctx.strokeStyle = '#ff3cac'; ctx.lineWidth = 1.5; ctx.setLineDash([5,3]);
        ctx.strokeRect(sx+0.5, sy+0.5, sw, sh); ctx.setLineDash([]);
        ctx.fillStyle = 'rgba(255,60,172,0.05)'; ctx.fillRect(sx, sy, sw, sh);
        // 8 edge+corner resize handles
        const hpts: [number,number,string][] = [
          [sx,    sy,    'nw'],[sx+sw/2, sy,    'n'],[sx+sw, sy,    'ne'],
          [sx+sw, sy+sh/2,'e'],[sx+sw, sy+sh,  'se'],[sx+sw/2,sy+sh,'s'],
          [sx,    sy+sh, 'sw'],[sx,    sy+sh/2, 'w'],
        ];
        ctx.fillStyle = '#ff3cac';
        for (const [hx,hy,] of hpts) { ctx.fillRect(hx-4, hy-4, 8, 8); }
        // Rotation handle above top-center
        const rotX = sx+sw/2, rotY = sy-24;
        ctx.strokeStyle = '#ff3cac'; ctx.lineWidth = 1; ctx.beginPath();
        ctx.moveTo(sx+sw/2, sy-4); ctx.lineTo(rotX, rotY-8); ctx.stroke();
        ctx.fillStyle = '#ff3cac'; ctx.beginPath(); ctx.arc(rotX, rotY-8, 5, 0, Math.PI*2); ctx.fill();
        // Rotation angle badge
        if (this.selRotAngle !== 0) {
          ctx.fillStyle = 'rgba(0,0,0,0.7)'; ctx.fillRect(rotX-18, rotY-28, 36, 14);
          ctx.fillStyle = '#ff3cac'; ctx.font = 'bold 9px monospace';
          ctx.fillText(`${Math.round(this.selRotAngle)}°`, rotX-14, rotY-18);
        }
        // Size badge
        ctx.fillStyle = 'rgba(0,0,0,0.6)'; ctx.fillRect(sx+2, sy+2, 60, 14);
        ctx.fillStyle = '#ff3cac'; ctx.font = 'bold 9px monospace';
        ctx.fillText(`${Math.abs(c2-c1)+1}×${Math.abs(r2-r1)+1}`, sx+5, sy+12);
      }
    }

    // Info badge
    ctx.fillStyle = 'rgba(0,0,0,0.55)'; ctx.fillRect(this.ox+4, this.oy+4, 90, 16);
    ctx.fillStyle = acc; ctx.font = 'bold 9px monospace';
    ctx.fillText(`${Math.round(this.zoom*100)}%  ${width}×${height}`, this.ox+8, this.oy+14);

    // ── Draw peer cursors ──────────────────────────────────────────
    for (const peer of this.peers) {
      // Only show cursor if the peer is viewing the same frame as the local user
      if (peer.activeFrameId && peer.activeFrameId !== this.frameId) continue;
      if (peer.cursorX < 0 || peer.cursorY < 0) continue;
      const px = Math.round(peer.cursorX * cs + this.ox);
      const py = Math.round(peer.cursorY * cs + this.oy);
      // Tool ghost
      const toolEmoji = peer.activeTool === 'erase' ? '🧹' : peer.activeTool === 'fill' ? '🪣' : '🖌';
      // Cursor dot
      ctx.save();
      ctx.shadowBlur = 8; ctx.shadowColor = peer.color;
      ctx.strokeStyle = peer.color; ctx.fillStyle = peer.color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(px + cs/2, py + cs/2, Math.max(3, cs/2 + 1), 0, Math.PI*2);
      ctx.stroke();
      ctx.globalAlpha = 0.25;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.restore();
      // Name label
      ctx.save();
      const labelText = peer.avatar + ' ' + peer.username;
      ctx.font = 'bold 10px monospace';
      const tw = ctx.measureText(labelText).width;
      const lx = px + cs/2 + 6, ly = py - 4;
      ctx.fillStyle = peer.color + 'cc';
      ctx.fillRect(lx - 2, ly - 11, tw + 8, 14);
      ctx.fillStyle = '#000';
      ctx.fillText(labelText, lx + 2, ly);
      ctx.restore();
    }
  }

  private drawPixelGrid(ctx: CanvasRenderingContext2D, pixels: string[][], width: number, height: number, cs: number): void {
    for (let y = 0; y < height && y < pixels.length; y++) {
      const row = pixels[y];
      for (let x = 0; x < width && x < row.length; x++) {
        const px = row[x];
        if (!px || px === '#00000000' || px === 'transparent') continue;
        ctx.fillStyle = px;
        ctx.fillRect(Math.round(x*cs+this.ox), Math.round(y*cs+this.oy), Math.ceil(cs), Math.ceil(cs));
      }
    }
  }

  private drawHoverBrushPreview(
    ctx: CanvasRenderingContext2D,
    hover: Pt,
    width: number,
    height: number,
    cs: number
  ): void {
    const bs = this.brushSize;
    const half = Math.floor(bs / 2);
    const cells = new Set<string>();
    for (const sp of this.symmetryPoints(hover)) {
      for (let dr = -half; dr < bs - half; dr++) {
        for (let dc = -half; dc < bs - half; dc++) {
          const r = sp.y + dr;
          const c = sp.x + dc;
          if (r >= 0 && r < height && c >= 0 && c < width) cells.add(`${r},${c}`);
        }
      }
    }

    ctx.save();
    if (this.tool === 'erase') {
      // Erase preview: neutral light tint so users can see target area clearly.
      ctx.fillStyle = '#ffffff';
      ctx.globalAlpha = 0.18;
    } else {
      ctx.fillStyle = this.color;
      ctx.globalAlpha = 0.35;
    }
    for (const key of cells) {
      const [r, c] = key.split(',').map(Number);
      const x = Math.round(c * cs + this.ox);
      const y = Math.round(r * cs + this.oy);
      ctx.fillRect(x, y, Math.ceil(cs), Math.ceil(cs));
    }
    ctx.restore();
  }

  private drawShapePreview(ctx: CanvasRenderingContext2D, cs: number): void {
    if (!this.shapeStart || !this.shapeCurrent) return;
    const a = this.shapeStart, b = this.shapeCurrent;
    // Update live dimensions
    this.shapeDimsVisible = true;
    this.shapePreviewDims = {
      w: Math.abs(b.x-a.x)+1, h: Math.abs(b.y-a.y)+1,
      r: Math.round(Math.hypot(b.x-a.x, b.y-a.y)),
      rx: Math.round(Math.abs(b.x-a.x)/2), ry: Math.round(Math.abs(b.y-a.y)/2),
      base: Math.abs(b.x-a.x)+1, height: Math.abs(b.y-a.y)+1,
      length: Math.round(Math.hypot(b.x-a.x, b.y-a.y)),
    };
    const pts = this.getShapePixels(a, b);
    ctx.save(); ctx.globalAlpha = 0.7;
    for (const p of pts) {
      ctx.fillStyle = this.color;
      ctx.fillRect(Math.round(p.x*cs+this.ox), Math.round(p.y*cs+this.oy), Math.ceil(cs), Math.ceil(cs));
    }
    ctx.restore();
  }

  // ────────────────────────────────────────────────────────────────
  //  SHAPE ALGORITHMS
  // ────────────────────────────────────────────────────────────────
  private getShapePixels(a: Pt, b: Pt): Pt[] {
    switch (this.tool) {
      case 'line':     return this.bresenham(a, b);
      case 'rect':     return this.shapeFillMode === 'filled' ? this.rectFill(a, b) : this.rectOutline(a, b);
      case 'circle':   return this.shapeFillMode === 'filled' ? this.circleFill(a, b) : this.circleOutline(a, b);
      case 'ellipse':  return this.shapeFillMode === 'filled' ? this.ellipseFill(a, b) : this.ellipseOutline(a, b);
      case 'triangle': return this.shapeFillMode === 'filled' ? this.triangleFill(a, b) : this.triangleOutline(a, b);
      default: return [];
    }
  }

  private bresenham(a: Pt, b: Pt): Pt[] {
    const pts: Pt[] = [];
    let { x: x0, y: y0 } = a; const { x: x1, y: y1 } = b;
    const dx = Math.abs(x1-x0), dy = Math.abs(y1-y0);
    const sx = x0<x1?1:-1, sy = y0<y1?1:-1;
    let err = dx-dy;
    while(true) {
      pts.push({x:x0,y:y0});
      if(x0===x1&&y0===y1) break;
      const e2=2*err;
      if(e2>-dy){err-=dy;x0+=sx;}
      if(e2<dx){err+=dx;y0+=sy;}
    }
    return pts;
  }

  private rectOutline(a: Pt, b: Pt): Pt[] {
    const pts: Pt[] = [];
    const x0=Math.min(a.x,b.x),x1=Math.max(a.x,b.x),y0=Math.min(a.y,b.y),y1=Math.max(a.y,b.y);
    for(let x=x0;x<=x1;x++){pts.push({x,y:y0});pts.push({x,y:y1});}
    for(let y=y0+1;y<y1;y++){pts.push({x:x0,y});pts.push({x:x1,y});}
    return pts;
  }

  private rectFill(a: Pt, b: Pt): Pt[] {
    const pts: Pt[] = [];
    const x0=Math.min(a.x,b.x),x1=Math.max(a.x,b.x),y0=Math.min(a.y,b.y),y1=Math.max(a.y,b.y);
    for(let y=y0;y<=y1;y++) for(let x=x0;x<=x1;x++) pts.push({x,y});
    return pts;
  }

  private circleOutline(a: Pt, b: Pt): Pt[] {
    const cx=a.x,cy=a.y,r=Math.round(Math.hypot(b.x-a.x,b.y-a.y));
    const pts: Pt[] = []; const set = new Set<string>();
    const add = (x:number,y:number) => { const k=`${x},${y}`; if(!set.has(k)){set.add(k);pts.push({x,y});} };
    let x=0,y=r,d=3-2*r;
    while(x<=y){
      [[ cx+x,cy+y],[cx-x,cy+y],[cx+x,cy-y],[cx-x,cy-y],[cx+y,cy+x],[cx-y,cy+x],[cx+y,cy-x],[cx-y,cy-x]].forEach(([px,py])=>add(px,py));
      if(d<0)d+=4*x+6; else{d+=4*(x-y)+10;y--;}x++;
    }
    return pts;
  }

  private circleFill(a: Pt, b: Pt): Pt[] {
    const cx=a.x,cy=a.y,r=Math.round(Math.hypot(b.x-a.x,b.y-a.y));
    const pts: Pt[] = [];
    for(let y=cy-r;y<=cy+r;y++) for(let x=cx-r;x<=cx+r;x++) if((x-cx)*(x-cx)+(y-cy)*(y-cy)<=r*r) pts.push({x,y});
    return pts;
  }

  private ellipseOutline(a: Pt, b: Pt): Pt[] {
    const cx=(a.x+b.x)/2,cy=(a.y+b.y)/2;
    const rx=Math.abs(b.x-a.x)/2,ry=Math.abs(b.y-a.y)/2;
    const pts: Pt[] = []; const set = new Set<string>();
    const steps=Math.max(rx,ry)*8||16;
    for(let i=0;i<steps;i++){
      const t=(i/steps)*2*Math.PI;
      const x=Math.round(cx+rx*Math.cos(t)), y=Math.round(cy+ry*Math.sin(t));
      const k=`${x},${y}`; if(!set.has(k)){set.add(k);pts.push({x,y});}
    }
    return pts;
  }

  private ellipseFill(a: Pt, b: Pt): Pt[] {
    const cx=(a.x+b.x)/2,cy=(a.y+b.y)/2;
    const rx=Math.abs(b.x-a.x)/2,ry=Math.abs(b.y-a.y)/2;
    const pts: Pt[] = [];
    if(rx<0.5||ry<0.5) return pts;
    for(let y=Math.floor(cy-ry);y<=Math.ceil(cy+ry);y++)
      for(let x=Math.floor(cx-rx);x<=Math.ceil(cx+rx);x++)
        if(((x-cx)/rx)**2+((y-cy)/ry)**2<=1) pts.push({x,y});
    return pts;
  }

  private triangleOutline(a: Pt, b: Pt): Pt[] {
    const apex={x:Math.round((a.x+b.x)/2),y:a.y};
    const bl={x:a.x,y:b.y}, br={x:b.x,y:b.y};
    return [...this.bresenham(apex,bl),...this.bresenham(bl,br),...this.bresenham(br,apex)];
  }

  private triangleFill(a: Pt, b: Pt): Pt[] {
    const outline=this.triangleOutline(a,b);
    const pts:Pt[]=[...outline];
    const byRow=new Map<number,{min:number,max:number}>();
    for(const p of outline){const r=byRow.get(p.y)??{min:Infinity,max:-Infinity};byRow.set(p.y,{min:Math.min(r.min,p.x),max:Math.max(r.max,p.x)});}
    byRow.forEach(({min,max},y)=>{for(let x=min+1;x<max;x++)pts.push({x,y});});
    return pts;
  }

  placeExactShape(opts: {tool:string;w:number;h:number;r:number}): void {
    if (!this.frameId || !this.settings || !this.frame) return;
    const { width, height } = this.settings.Resolution;
    const cx = Math.floor(width/2), cy = Math.floor(height/2);
    const half_w = Math.floor(opts.w/2), half_h = Math.floor(opts.h/2);
    let start: Pt, end: Pt;
    if (opts.tool === 'circle') {
      start = { x: cx, y: cy };
      end   = { x: cx + opts.r, y: cy };
    } else {
      start = { x: cx - half_w, y: cy - half_h };
      end   = { x: cx + half_w, y: cy + half_h };
    }
    // Temporarily set shape tool state and commit
    const savedStart = this.shapeStart, savedCur = this.shapeCurrent;
    this.shapeStart = start; this.shapeCurrent = end;
    const pts = this.getShapePixels(start, end);
    const newPixels = this.frame.pixels.map(r => [...r]);
    const allPts = new Set<string>();
    for (const p of pts) for (const sp of this.symmetryPoints(p)) allPts.add(`${sp.x},${sp.y}`);
    allPts.forEach(k => {
      const [x,y] = k.split(',').map(Number);
      if (y>=0&&y<height&&x>=0&&x<width) newPixels[y][x] = this.color;
    });
    this.store.dispatch(FrameActions.replaceFramePixels({ frameId: this.frameId!, pixels: newPixels }));
    this.shapeStart = savedStart; this.shapeCurrent = savedCur;
    this.draw();
  }

  // ────────────────────────────────────────────────────────────────
  //  COORDINATE HELPERS
  // ────────────────────────────────────────────────────────────────
  private getCell(cx: number, cy: number): Pt | null {
    if (!this.settings) return null;
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const cs = (this.settings.cellSize ?? 8) * this.zoom;
    const x = Math.floor((cx - rect.left - this.ox) / cs);
    const y = Math.floor((cy - rect.top - this.oy) / cs);
    const { width, height } = this.settings.Resolution;
    return (x >= 0 && y >= 0 && x < width && y < height) ? { x, y } : null;
  }

  private getCellUnbounded(cx: number, cy: number): Pt {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const cs = (this.settings!.cellSize ?? 8) * this.zoom;
    return {
      x: Math.floor((cx - rect.left - this.ox) / cs),
      y: Math.floor((cy - rect.top - this.oy) / cs),
    };
  }

  private isReadOnlyInRoom(): boolean {
    return this.collabSvc.isInRoom$.value && !this.collabSvc.can('draw');
  }

  private getEditorWrap(): HTMLElement | null {
    return this.canvasRef?.nativeElement?.closest('.editor-wrap') as HTMLElement | null;
  }

  private setCursor(cursor: string): void {
    const wrap = this.getEditorWrap();
    const canvas = this.canvasRef?.nativeElement as HTMLElement | undefined;
    if (wrap) wrap.style.cursor = cursor;
    if (canvas) canvas.style.cursor = cursor;
  }

  private getResizeCursor(handle: 'nw'|'n'|'ne'|'e'|'se'|'s'|'sw'|'w'): string {
    const map: Record<'nw'|'n'|'ne'|'e'|'se'|'s'|'sw'|'w', string> = {
      nw: 'nwse-resize',
      n: 'ns-resize',
      ne: 'nesw-resize',
      e: 'ew-resize',
      se: 'nwse-resize',
      s: 'ns-resize',
      sw: 'nesw-resize',
      w: 'ew-resize',
    };
    return map[handle];
  }

  /** Hover cursor for floating clip handles / body (shape paste preview). */
  private updateFloatingClipHoverCursor(clientX: number, clientY: number): void {
    const fc = this.floatingClip;
    if (!fc || !this.settings) return;
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const mx = clientX - rect.left, my = clientY - rect.top;
    const fw = fc.pixels[0]?.length ?? 0, fh = fc.pixels.length;
    const cs2 = (this.settings.cellSize ?? 8) * this.zoom;
    const fcSx = Math.round(fc.x * cs2 + this.ox), fcSy = Math.round(fc.y * cs2 + this.oy);
    const fcSw = Math.round(fw * cs2), fcSh = Math.round(fh * cs2);
    const fcHandles: [number, number, 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'][] = [
      [fcSx, fcSy, 'nw'],
      [fcSx + fcSw / 2, fcSy, 'n'],
      [fcSx + fcSw, fcSy, 'ne'],
      [fcSx + fcSw, fcSy + fcSh / 2, 'e'],
      [fcSx + fcSw, fcSy + fcSh, 'se'],
      [fcSx + fcSw / 2, fcSy + fcSh, 's'],
      [fcSx, fcSy + fcSh, 'sw'],
      [fcSx, fcSy + fcSh / 2, 'w'],
    ];
    for (const [hx, hy, hname] of fcHandles) {
      if (Math.abs(mx - hx) < 7 && Math.abs(my - hy) < 7) {
        this.setCursor(this.getResizeCursor(hname));
        return;
      }
    }
    const insideX = mx >= fcSx && mx <= fcSx + fcSw;
    const insideY = my >= fcSy && my <= fcSy + fcSh;
    this.setCursor(insideX && insideY ? 'grab' : 'default');
  }

  private getToolCursor(tool = this.tool): string {
    void tool;
    return 'crosshair';
  }

  // ────────────────────────────────────────────────────────────────
  //  PAINT + SYMMETRY
  // ────────────────────────────────────────────────────────────────

  private updateCursorForPosition(cx: number, cy: number): void {
    if (!this.selection || this.tool !== 'select' || !this.settings) {
      this.setCursor(this.getToolCursor());
      return;
    }
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const mx = cx - rect.left, my = cy - rect.top;
    const sel = this.normSel();
    if (!sel) {
      this.setCursor(this.getToolCursor());
      return;
    }
    const cs = (this.settings.cellSize ?? 8) * this.zoom;
    const sx = Math.round(sel.c1*cs+this.ox), sy = Math.round(sel.r1*cs+this.oy);
    const sw = Math.round((sel.c2-sel.c1+1)*cs), sh = Math.round((sel.r2-sel.r1+1)*cs);

    const cursors: [number,number,string][] = [
      [sx,    sy,    this.getResizeCursor('nw')],[sx+sw/2, sy,    this.getResizeCursor('n')],[sx+sw, sy,    this.getResizeCursor('ne')],
      [sx+sw, sy+sh/2,this.getResizeCursor('e')],[sx+sw, sy+sh,  this.getResizeCursor('se')],[sx+sw/2,sy+sh,this.getResizeCursor('s')],
      [sx,    sy+sh, this.getResizeCursor('sw')],[sx,    sy+sh/2, this.getResizeCursor('w')],
    ];
    // Rotation handle
    if (Math.hypot(mx - (sx+sw/2), my - (sy-32)) < 10) { this.setCursor('crosshair'); return; }
    for (const [hx,hy,cur] of cursors) {
      if (Math.abs(mx-hx) < 8 && Math.abs(my-hy) < 8) { this.setCursor(cur); return; }
    }
    // Inside selection
    if (mx>sx && mx<sx+sw && my>sy && my<sy+sh) { this.setCursor('grab'); return; }
    this.setCursor(this.getToolCursor());
  }

  private updateSelectionCursor(e: MouseEvent): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas || !this.settings || !this.selection) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left, my = e.clientY - rect.top;
    const sel = this.normSel();
    if (!sel) return;
    const cs = (this.settings.cellSize ?? 8) * this.zoom;
    const sx = Math.round(sel.c1*cs+this.ox), sy = Math.round(sel.r1*cs+this.oy);
    const sw = Math.round((sel.c2-sel.c1+1)*cs), sh = Math.round((sel.r2-sel.r1+1)*cs);
    const cursorMap: Record<string, string> = {
      'nw': this.getResizeCursor('nw'),
      'n': this.getResizeCursor('n'),
      'ne': this.getResizeCursor('ne'),
      'e': this.getResizeCursor('e'),
      'se': this.getResizeCursor('se'),
      's': this.getResizeCursor('s'),
      'sw': this.getResizeCursor('sw'),
      'w': this.getResizeCursor('w'),
      'rot':'crosshair'
    };
    const handles: [number,number,string][] = [
      [sx,sy,'nw'],[sx+sw/2,sy,'n'],[sx+sw,sy,'ne'],
      [sx+sw,sy+sh/2,'e'],[sx+sw,sy+sh,'se'],[sx+sw/2,sy+sh,'s'],
      [sx,sy+sh,'sw'],[sx,sy+sh/2,'w'],
      [sx+sw/2,sy-32,'rot'],
    ];
    let found = false;
    for (const [hx,hy,hname] of handles) {
      if (Math.abs(mx-hx) < 8 && Math.abs(my-hy) < 8) {
        this.setCursor(cursorMap[hname] ?? this.getToolCursor());
        found = true; break;
      }
    }
    if (!found) {
      const cell = this.getCellUnbounded(e.clientX, e.clientY);
      if (cell.x>=sel.c1&&cell.x<=sel.c2&&cell.y>=sel.r1&&cell.y<=sel.r2)
        this.setCursor('grab');
      else
        this.setCursor(this.getToolCursor());
    }
  }

  private symmetryPoints(p: Pt): Pt[] {
    if (!this.settings || this.symmetryMode === 'none') return [p];
    const { width, height } = this.settings.Resolution;
    const cx = (width - 1) / 2, cy = (height - 1) / 2;
    const dx = p.x - cx, dy = p.y - cy;
    const seen = new Set<string>();
    const pts: Pt[] = [];
    const add = (x: number, y: number) => {
      const rx = Math.round(x), ry = Math.round(y);
      const k = `${rx},${ry}`;
      if (!seen.has(k)) { seen.add(k); pts.push({x: rx, y: ry}); }
    };
    add(p.x, p.y);

    if (this.symmetryMode === 'vertical')   { add(cx - dx, p.y); }
    if (this.symmetryMode === 'horizontal') { add(p.x, cy - dy); }
    if (this.symmetryMode === 'quad') {
      add(cx - dx, p.y); add(p.x, cy - dy); add(cx - dx, cy - dy);
    }
    if (this.symmetryMode === 'diagonal-fwd') {
      // Reflect in normalized space so non-square canvases stay consistent.
      const sx = Math.max(1, width - 1);
      const sy = Math.max(1, height - 1);
      const nx = dx / sx;
      const ny = dy / sy;
      // "/" mirror (y = -x in centered coords): (x,y) -> (-y,-x)
      add(cx - ny * sx, cy - nx * sy);
    }
    if (this.symmetryMode === 'diagonal-bk') {
      const sx = Math.max(1, width - 1);
      const sy = Math.max(1, height - 1);
      const nx = dx / sx;
      const ny = dy / sy;
      // "\" mirror (y = x in centered coords): (x,y) -> (y,x)
      add(cx + ny * sx, cy + nx * sy);
    }

    // Radial/star symmetry
    const radialMatch = this.symmetryMode.match(/^radial-(\d+)$/);
    const starMatch   = this.symmetryMode.match(/^star-(\d+)$/);
    const n = radialMatch ? +radialMatch[1] : (starMatch ? +starMatch[1] * 2 : 0);
    if (n > 0) {
      const angle = Math.atan2(dy, dx);
      const r = Math.hypot(dx, dy);
      const step = (Math.PI * 2) / n;
      for (let i = 1; i < n; i++) {
        const a = angle + step * i;
        add(cx + r * Math.cos(a), cy + r * Math.sin(a));
      }
    }
    return pts.filter(pt => pt.x >= 0 && pt.y >= 0 && pt.x < width && pt.y < height);
  }

  private paintAt(p: Pt): void {
    if (!this.frameId || !this.settings) return;
    const { width, height } = this.settings.Resolution;
    const paintColor = this.tool === 'erase' ? '#00000000' : this.color;
    const bs = this.brushSize;
    const half = Math.floor(bs / 2);

    const cells: { row: number; col: number; color: string }[] = [];
    for (const sp of this.symmetryPoints(p)) {
      for (let dr = -half; dr < bs - half; dr++) {
        for (let dc = -half; dc < bs - half; dc++) {
          const r = sp.y + dr, c = sp.x + dc;
          if (r >= 0 && r < height && c >= 0 && c < width) {
            cells.push({ row: r, col: c, color: paintColor });
          }
        }
      }
    }
    if (cells.length > 0) {
      this.store.dispatch(FrameActions.paintPixels({ frameId: this.frameId!, cells }));
    }
  }

  private applyPaint(cx: number, cy: number): void {
    this.didDraw = true;
    if (!this.frameId) return;
    const cell = this.getCell(cx, cy);
    if (!cell) return;

    if (this.tool === 'pick') {
      const c = this.frame?.pixels[cell.y]?.[cell.x];
      if (c) { this.store.dispatch(ToolActions.setActiveColor({ color: c })); this.store.dispatch(ToolActions.addRecentColor({ color: c })); }
      return;
    }
    if (this.tool === 'fill') {
      const points = this.symmetryPoints(cell)
        .map(sp => ({ row: sp.y, col: sp.x }))
        .filter((p, i, arr) => arr.findIndex(x => x.row === p.row && x.col === p.col) === i);
      if (points.length <= 1) {
        this.store.dispatch(FrameActions.fillPixels({ frameId: this.frameId, row: cell.y, col: cell.x, color: this.color }));
      } else {
        this.store.dispatch(FrameActions.fillPixelsSymmetry({ frameId: this.frameId, points, color: this.color }));
      }
      this.lastPaintCell = cell;
      return;
    }
    // Interpolate from last cell to current cell so no pixels are skipped on fast moves.
    // Collect all cells from the entire interpolated line and dispatch ONE batch action.
    if (this.lastPaintCell && this.isDrawing) {
      const pts = this.bresenhamLine(this.lastPaintCell, { x: cell.x, y: cell.y });
      // Build one flat cells array across all interpolated points
      const allCells: { row: number; col: number; color: string }[] = [];
      const { width, height } = this.settings!.Resolution;
      const paintColor = this.tool === 'erase' ? '#00000000' : this.color;
      const bs = this.brushSize;
      const half = Math.floor(bs / 2);
      const seen = new Set<string>();
      for (const p of pts) {
        for (const sp of this.symmetryPoints(p)) {
          for (let dr = -half; dr < bs - half; dr++) {
            for (let dc = -half; dc < bs - half; dc++) {
              const r = sp.y + dr, c = sp.x + dc;
              const key = `${r},${c}`;
              if (r >= 0 && r < height && c >= 0 && c < width && !seen.has(key)) {
                seen.add(key);
                allCells.push({ row: r, col: c, color: paintColor });
              }
            }
          }
        }
      }
      if (allCells.length > 0) {
        this.store.dispatch(FrameActions.paintPixels({ frameId: this.frameId!, cells: allCells }));
      }
    } else {
      this.paintAt(cell);
    }
    this.lastPaintCell = cell;
  }

  private bresenhamLine(a: {x:number;y:number}, b: {x:number;y:number}): {x:number;y:number}[] {
    const pts: {x:number;y:number}[] = [];
    let {x:x0, y:y0} = a; const {x:x1, y:y1} = b;
    const dx=Math.abs(x1-x0), dy=Math.abs(y1-y0);
    const sx=x0<x1?1:-1, sy=y0<y1?1:-1;
    let err=dx-dy;
    while(true){
      pts.push({x:x0,y:y0});
      if(x0===x1&&y0===y1)break;
      const e2=2*err;
      if(e2>-dy){err-=dy;x0+=sx;}
      if(e2<dx){err+=dx;y0+=sy;}
    }
    return pts;
  }

  // ────────────────────────────────────────────────────────────────
  //  SELECTION OPERATIONS
  // ────────────────────────────────────────────────────────────────
  private normSel(): SelectionRect | null {
    if (!this.selection) return null;
    return {
      r1: Math.min(this.selection.r1, this.selection.r2), c1: Math.min(this.selection.c1, this.selection.c2),
      r2: Math.max(this.selection.r1, this.selection.r2), c2: Math.max(this.selection.c1, this.selection.c2),
    };
  }

  private extractPixelsFromRect(sel: SelectionRect, pixels: string[][]): string[][] {
    const out: string[][] = [];
    for (let r = sel.r1; r <= sel.r2; r++) {
      const row: string[] = [];
      for (let c = sel.c1; c <= sel.c2; c++) {
        row.push(pixels[r]?.[c] ?? '#00000000');
      }
      out.push(row);
    }
    return out;
  }

  private beginSelectionTransform(sel: SelectionRect): void {
    if (!this.frame) return;
    // Use the active cel's pixels if available; fall back to composited frame pixels.
    const sourceGrid = this.activeCelPixels ?? this.frame.pixels;
    this.transformOriginSel = { ...sel };
    this.transformSourcePixels = this.extractPixelsFromRect(sel, sourceGrid);
    this.transformPreviewPixels = this.transformSourcePixels;
    this.transformPreviewRow = sel.r1;
    this.transformPreviewCol = sel.c1;
  }

  private scalePixelsNearest(source: string[][], dstW: number, dstH: number): string[][] {
    const srcH = source.length;
    const srcW = source[0]?.length ?? 0;
    const out: string[][] = Array.from({ length: Math.max(1, dstH) }, () => Array(Math.max(1, dstW)).fill('#00000000'));
    for (let dr = 0; dr < out.length; dr++) {
      for (let dc = 0; dc < out[dr].length; dc++) {
        const sr = Math.min(srcH - 1, Math.max(0, Math.floor(dr * srcH / Math.max(1, dstH))));
        const sc = Math.min(srcW - 1, Math.max(0, Math.floor(dc * srcW / Math.max(1, dstW))));
        out[dr][dc] = source[sr]?.[sc] ?? '#00000000';
      }
    }
    return out;
  }

  private isTransparentPixel(px: string | undefined): boolean {
    return !px || px === 'transparent' || px === '#00000000';
  }

  private trimTransparentPadding(pixels: string[][]): { pixels: string[][]; rowOffset: number; colOffset: number } {
    const h = pixels.length;
    const w = pixels[0]?.length ?? 0;
    if (h === 0 || w === 0) return { pixels, rowOffset: 0, colOffset: 0 };

    let minR = h, minC = w, maxR = -1, maxC = -1;
    for (let r = 0; r < h; r++) {
      for (let c = 0; c < w; c++) {
        if (!this.isTransparentPixel(pixels[r]?.[c])) {
          if (r < minR) minR = r;
          if (c < minC) minC = c;
          if (r > maxR) maxR = r;
          if (c > maxC) maxC = c;
        }
      }
    }

    // Entire output is transparent; keep as-is to avoid invalid dimensions.
    if (maxR < minR || maxC < minC) return { pixels, rowOffset: 0, colOffset: 0 };

    const trimmed: string[][] = [];
    for (let r = minR; r <= maxR; r++) trimmed.push(pixels[r].slice(minC, maxC + 1));
    return { pixels: trimmed, rowOffset: minR, colOffset: minC };
  }

  private pixelToRgba(pixel: string): [number, number, number, number] {
    if (!pixel || pixel === 'transparent' || pixel === '#00000000') return [0, 0, 0, 0];
    const hex = pixel.startsWith('#') ? pixel.slice(1) : pixel;
    if (hex.length === 8) {
      return [
        parseInt(hex.slice(0, 2), 16) || 0,
        parseInt(hex.slice(2, 4), 16) || 0,
        parseInt(hex.slice(4, 6), 16) || 0,
        parseInt(hex.slice(6, 8), 16) || 0,
      ];
    }
    if (hex.length === 6) {
      return [
        parseInt(hex.slice(0, 2), 16) || 0,
        parseInt(hex.slice(2, 4), 16) || 0,
        parseInt(hex.slice(4, 6), 16) || 0,
        255,
      ];
    }
    return [0, 0, 0, 0];
  }

  private rgbaToPixel(r: number, g: number, b: number, a: number): string {
    if (a <= 1) return '#00000000';
    const hex = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
    if (a >= 254) return `#${hex(r)}${hex(g)}${hex(b)}`;
    return `#${hex(r)}${hex(g)}${hex(b)}${hex(a)}`;
  }

  private rotatePixelsResampled(source: string[][], angleDeg: number): string[][] {
    const srcH = source.length;
    const srcW = source[0]?.length ?? 0;
    if (srcW === 0 || srcH === 0) return source;
    const rad = angleDeg * Math.PI / 180;
    const absCos = Math.abs(Math.cos(rad));
    const absSin = Math.abs(Math.sin(rad));
    const dstW = Math.max(1, Math.ceil(srcW * absCos + srcH * absSin));
    const dstH = Math.max(1, Math.ceil(srcW * absSin + srcH * absCos));
    const out: string[][] = Array.from({ length: dstH }, () => Array(dstW).fill('#00000000'));
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const srcCx = (srcW - 1) / 2;
    const srcCy = (srcH - 1) / 2;
    const dstCx = (dstW - 1) / 2;
    const dstCy = (dstH - 1) / 2;

    // Pixel-art rotation: nearest-neighbor inverse mapping to preserve exact colors.
    for (let r = 0; r < dstH; r++) {
      for (let c = 0; c < dstW; c++) {
        const dx = c - dstCx;
        const dy = r - dstCy;
        const srcX = dx * cos + dy * sin + srcCx;
        const srcY = -dx * sin + dy * cos + srcCy;
        const sx = Math.round(srcX);
        const sy = Math.round(srcY);
        if (sy >= 0 && sy < srcH && sx >= 0 && sx < srcW) {
          out[r][c] = source[sy]?.[sx] ?? '#00000000';
        }
      }
    }
    return out;
  }

  copySelection(): void {
    const sel = this.normSel();
    if (!sel || !this.frame) return;
    const pixels = this.extractPixelsFromRect(sel, this.frame.pixels);
    this.floatingClip = { pixels, x: sel.c1, y: sel.r1, isDragging: false, dragOffsetX: 0, dragOffsetY: 0 };
    this.draw();
  }

  cutSelection(): void {
    const sel = this.normSel();
    if (!sel || !this.frame || !this.frameId) return;
    const pixels = this.extractPixelsFromRect(sel, this.frame.pixels);
    this.floatingClip = { pixels, x: sel.c1, y: sel.r1, isDragging: false, dragOffsetX: 0, dragOffsetY: 0 };
    this.store.dispatch(FrameActions.cutSelection({ frameId: this.frameId, r1: sel.r1, c1: sel.c1, r2: sel.r2, c2: sel.c2 }));
    this.selection = null;
  }

  get floatW(): number { return this.floatingClip?.pixels[0]?.length ?? 0; }
  get floatH(): number { return this.floatingClip?.pixels.length ?? 0; }
  // Convenience getters for shape-specific dimensions shown in the toolbar
  get floatR(): number  { return Math.floor(Math.min(this.floatW, this.floatH) / 2); }
  get floatRx(): number { return Math.floor(this.floatW / 2); }
  get floatRy(): number { return Math.floor(this.floatH / 2); }

  /**
   * Re-renders a shape at a given pixel size without any bitmap scaling.
   * Returns the pixel grid and the actual (snapped) dimensions used.
   * Falls back to nearest-neighbour scale for paste clips that have no shapeType.
   */
  private generateShapeClipPixels(
    shapeType: ToolType, fillMode: ShapeFillMode, color: string,
    w: number, h: number
  ): { pixels: string[][]; w: number; h: number } {
    // Temporarily borrow tool/fillMode so getShapePixels works
    const savedTool = this.tool;
    const savedFill = this.shapeFillMode;
    (this as any).tool = shapeType;
    this.shapeFillMode = fillMode;

    let pts: Pt[];
    let outW = Math.max(1, w), outH = Math.max(1, h);

    if (shapeType === 'circle') {
      // Circle uses center+edge points; snap to square bounding box (odd side = 2r+1)
      const r = Math.max(1, Math.round(Math.min(w, h) / 2));
      outW = outH = 2 * r + 1;
      pts = this.getShapePixels({ x: r, y: r }, { x: 2 * r, y: r });
    } else {
      pts = this.getShapePixels({ x: 0, y: 0 }, { x: outW - 1, y: outH - 1 });
    }

    (this as any).tool = savedTool;
    this.shapeFillMode = savedFill;

    const pixels: string[][] = Array.from({ length: outH }, () => Array(outW).fill('#00000000'));
    for (const p of pts) {
      if (p.y >= 0 && p.y < outH && p.x >= 0 && p.x < outW) pixels[p.y][p.x] = color;
    }
    return { pixels, w: outW, h: outH };
  }

  resizeFloatingClip(dim: 'w'|'h'|'r'|'rx'|'ry', val: number): void {
    if (!this.floatingClip) return;
    const fc = this.floatingClip;
    if (!fc.shapeType) {
      // Paste clip - fall back to bitmap scale
      const newW = (dim === 'w') ? Math.max(1, val) : this.floatW;
      const newH = (dim === 'h') ? Math.max(1, val) : this.floatH;
      if (newW === this.floatW && newH === this.floatH) return;
      this.floatingClip = { ...fc, pixels: this.scalePixelsNearest(fc.pixels, newW, newH) };
      this.draw(); return;
    }
    // Shape clip - compute target w/h from the dimension that changed
    let newW = this.floatW, newH = this.floatH;
    if (dim === 'r')  { const r = Math.max(1, val); newW = newH = 2 * r + 1; }
    if (dim === 'rx') { newW = Math.max(1, val) * 2; }
    if (dim === 'ry') { newH = Math.max(1, val) * 2; }
    if (dim === 'w')  { newW = Math.max(1, val); }
    if (dim === 'h')  { newH = Math.max(1, val); }
    const result = this.generateShapeClipPixels(fc.shapeType, fc.shapeFillMode!, fc.shapeColor!, newW, newH);
    this.floatingClip = { ...fc, pixels: result.pixels };
    this.draw();
  }

  stampFloatingClip(): void {
    if (!this.floatingClip || !this.frameId) return;
    this.store.dispatch(FrameActions.applySelectionPaste({ frameId: this.frameId, pixels: this.floatingClip.pixels, destRow: this.floatingClip.y, destCol: this.floatingClip.x }));
    this.floatingClip = null;
    this.draw();
  }

  pasteSelection(): void {
    if (this.floatingClip) { this.stampFloatingClip(); return; }
  }

  fillSelectionArea(): void {
    const sel = this.normSel();
    if (!sel || !this.frameId) return;
    this.store.dispatch(FrameActions.applySelectionFill({ frameId: this.frameId, r1: sel.r1, c1: sel.c1, r2: sel.r2, c2: sel.c2, color: this.color }));
  }

  clearSelectionArea(): void {
    const sel = this.normSel();
    if (!sel || !this.frameId) return;
    this.store.dispatch(FrameActions.applySelectionFill({ frameId: this.frameId, r1: sel.r1, c1: sel.c1, r2: sel.r2, c2: sel.c2, color: '#00000000' }));
  }

  flipSelectionH(): void {
    const sel = this.normSel();
    if (!sel || !this.frame || !this.frameId) return;
    const extracted = this.extractPixelsFromRect(sel, this.frame.pixels);
    const flipped = extracted.map(r => [...r].reverse());
    this.store.dispatch(FrameActions.applySelectionTransform({
      frameId: this.frameId,
      origin: sel,
      pixels: flipped,
      destRow: sel.r1,
      destCol: sel.c1
    }));
  }

  flipSelectionV(): void {
    const sel = this.normSel();
    if (!sel || !this.frame || !this.frameId) return;
    const extracted = this.extractPixelsFromRect(sel, this.frame.pixels);
    const flipped = [...extracted].reverse();
    this.store.dispatch(FrameActions.applySelectionTransform({
      frameId: this.frameId,
      origin: sel,
      pixels: flipped,
      destRow: sel.r1,
      destCol: sel.c1
    }));
  }

  rotateSelection90(cw = true): void {
    const sel = this.normSel();
    if (!sel || !this.frame || !this.frameId) return;
    const extracted = this.extractPixelsFromRect(sel, this.frame.pixels);
    const rows = extracted.length, cols = extracted[0]?.length ?? 0;
    const rotated: string[][] = Array.from({length: cols}, () => Array(rows).fill('#00000000'));
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
      if (cw) rotated[c][rows-1-r] = extracted[r][c];
      else    rotated[cols-1-c][r] = extracted[r][c];
    }
    this.store.dispatch(FrameActions.applySelectionTransform({
      frameId: this.frameId,
      origin: sel,
      pixels: rotated,
      destRow: sel.r1,
      destCol: sel.c1
    }));
    this.selection = {
      r1: sel.r1,
      c1: sel.c1,
      r2: sel.r1 + rotated.length - 1,
      c2: sel.c1 + (rotated[0]?.length ?? 0) - 1
    };
  }

  selectAll(): void {
    if (!this.settings) return;
    this.selection = { r1: 0, c1: 0, r2: this.settings.Resolution.height-1, c2: this.settings.Resolution.width-1 };
    this.draw();
  }

  deselect(): void {
    if (this.floatingClip) this.stampFloatingClip();
    this.selection = null;
    this.draw();
  }

  // ────────────────────────────────────────────────────────────────
  //  IMAGE IMPORT
  // ────────────────────────────────────────────────────────────────
  triggerImageImport(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file || !this.settings) return;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { width, height } = this.settings!.Resolution;
      const aspect = img.width / img.height;
      let sw = width, sh = Math.round(width / aspect);
      if (sh > height) { sh = height; sw = Math.round(height * aspect); }
      this.importConfig = { imgEl: img, x: 0, y: 0, scaleW: sw, scaleH: sh, opacity: 100 };
      this.importX = 0; this.importY = 0; this.importScaleW = sw; this.importScaleH = sh; this.importOpacity = 100;
      this.showImportModal = true;
      this.draw();
    };
    img.src = url;
    (event.target as HTMLInputElement).value = '';
  }

  updateImportConfig(): void {
    if (!this.importConfig) return;
    this.importConfig = { ...this.importConfig, x: this.importX, y: this.importY, scaleW: this.importScaleW, scaleH: this.importScaleH, opacity: this.importOpacity };
    this.draw();
  }

  commitImport(): void {
    if (!this.importConfig || !this.frameId || !this.settings) return;
    const { imgEl, x, y, scaleW, scaleH } = this.importConfig;
    const { width, height } = this.settings.Resolution;
    const off = document.createElement('canvas'); off.width = width; off.height = height;
    const ctx = off.getContext('2d')!; ctx.imageSmoothingEnabled = false;
    ctx.drawImage(imgEl, x, y, scaleW, scaleH);
    const imageData = ctx.getImageData(0, 0, width, height);
    const pixels: string[][] = [];
    for (let r = 0; r < height; r++) { pixels[r]=[];
      for (let c = 0; c < width; c++) { const i=(r*width+c)*4;
        pixels[r][c]='#'+[imageData.data[i],imageData.data[i+1],imageData.data[i+2]].map(v=>v.toString(16).padStart(2,'0')).join('');
      }
    }
    this.store.dispatch(FrameActions.replaceFramePixels({ frameId: this.frameId, pixels }));
    this.cancelImport();
  }

  cancelImport(): void { this.importConfig = null; this.showImportModal = false; this.draw(); }

  // ────────────────────────────────────────────────────────────────
  //  MOUSE EVENTS
  // ────────────────────────────────────────────────────────────────
  @HostListener('mousedown', ['$event'])
  onMouseDown(e: MouseEvent): void {
    // Guard: only handle clicks that originate from the canvas itself or editor-wrap background.
    // Clicks on toolbar overlays, import panel, hints etc. must NOT trigger draw/select.
    const target = e.target as HTMLElement;
    const canvas = this.canvasRef?.nativeElement;
    const isOnCanvas = target === canvas;
    const isOnEditorWrap = target.classList.contains('editor-wrap');
    const isOverlay = !!(target.closest('.sel-toolbar, .floating-hint, .poly-hint, .shape-dims, .import-overlay, .canvas-controls-hint, .sel-btn, .import-panel, .import-actions'));
    if (!isOnCanvas && !isOnEditorWrap && isOverlay) return;
    if (!isOnCanvas && !isOnEditorWrap && target.tagName === 'BUTTON') return;
    if (!isOnCanvas && !isOnEditorWrap && target.tagName === 'INPUT') return;
    if (!isOnCanvas && !isOnEditorWrap && target.tagName === 'LABEL') return;

    e.preventDefault();
    if (e.button === 1) { this.isMidPanning = true; this.isPanning = true; this.panStartX = e.clientX - this.ox; this.panStartY = e.clientY - this.oy; return; }
    // Space+click pan: capture grab offset here so the canvas stays anchored under the cursor
    if (this.isSpacePanning && e.button === 0) { this.panStartX = e.clientX - this.ox; this.panStartY = e.clientY - this.oy; return; }
    if (e.button !== 0) return;
    if (this.isReadOnlyInRoom()) return;

    // ── Image import: drag ghost to position ──
    if (this.showImportModal && this.importConfig) {
      const cell = this.getCellUnbounded(e.clientX, e.clientY);
      this.importDragging = true;
      this.importDragStart = { x: cell.x - this.importConfig.x, y: cell.y - this.importConfig.y };
      return;
    }

    // Interact with floating clipboard (resize handles, drag, or stamp on outside click)
    if (this.floatingClip) {
      const canvas = this.canvasRef.nativeElement;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      const fc = this.floatingClip;
      const fw = fc.pixels[0]?.length ?? 0, fh = fc.pixels.length;
      const cs = (this.settings?.cellSize ?? 8) * this.zoom;
      const fcSx = Math.round(fc.x*cs+this.ox), fcSy = Math.round(fc.y*cs+this.oy);
      const fcSw = Math.round(fw*cs), fcSh = Math.round(fh*cs);
      // Hit-test resize handles (8 positions, same order as draw)
      const fcHandles: [number,number,'nw'|'n'|'ne'|'e'|'se'|'s'|'sw'|'w'][] = [
        [fcSx,          fcSy,          'nw'],[fcSx+fcSw/2, fcSy,          'n'],[fcSx+fcSw,  fcSy,          'ne'],
        [fcSx+fcSw,     fcSy+fcSh/2,   'e' ],[fcSx+fcSw,  fcSy+fcSh,     'se'],[fcSx+fcSw/2, fcSy+fcSh,   's' ],
        [fcSx,          fcSy+fcSh,     'sw'],[fcSx,        fcSy+fcSh/2,   'w' ],
      ];
      let hitHandle: typeof this.fcResizeHandle = null;
      for (const [hx, hy, hname] of fcHandles) {
        if (Math.abs(mx-hx) < 7 && Math.abs(my-hy) < 7) { hitHandle = hname; break; }
      }
      if (hitHandle) {
        this.isFcResizing = true;
        this.fcResizeHandle = hitHandle;
        this.fcResizeStartBounds = { r1: fc.y, c1: fc.x, r2: fc.y + fh - 1, c2: fc.x + fw - 1 };
        this.fcResizeOrigPixels = fc.pixels.map(r => [...r]);
        return;
      }
      // Drag inside
      const cell = this.getCellUnbounded(e.clientX, e.clientY);
      if (cell.x >= fc.x && cell.x < fc.x+fw && cell.y >= fc.y && cell.y < fc.y+fh) {
        this.floatingClip = { ...fc, isDragging: true, dragOffsetX: cell.x - fc.x, dragOffsetY: cell.y - fc.y };
        return;
      } else {
        // Click outside floating clip → stamp it
        this.stampFloatingClip();
        // For shape tools, don't swallow the click - let it fall through to start a new shape drag
        const isShapeTool = ['rect','circle','ellipse','triangle','line','polygon'].includes(this.tool);
        if (!isShapeTool) return;
      }
    }

    // Select tool: hit-test handles, else drag inside = move, else new selection
    if (this.tool === 'select') {
      const canvas = this.canvasRef.nativeElement;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      const sel = this.normSel();

      // Check rotation handle first
      if (sel && this.settings) {
        const cs = (this.settings.cellSize ?? 8) * this.zoom;
        const sx = Math.round(sel.c1*cs+this.ox), sy = Math.round(sel.r1*cs+this.oy);
        const sw = Math.round((sel.c2-sel.c1+1)*cs);
        const rotX = sx+sw/2, rotY = sy-32;
        const distRot = Math.hypot(mx-rotX, my-rotY);
        if (distRot < 8) {
          // Start rotation drag
          this.selRotCenter = { x: sx+sw/2, y: sy + Math.round((sel.r2-sel.r1+1)*cs)/2 };
          this.selRotAngle = 0;
          this.beginSelectionTransform(sel);
          this.isSelTransforming = true;
          this.setCursor('crosshair');
          return;
        }
        // Check resize handles
        const sh = Math.round((sel.r2-sel.r1+1)*cs);
        const handles: [number,number,typeof this.selResizeHandle][] = [
          [sx,    sy,    'nw'],[sx+sw/2, sy,    'n'],[sx+sw, sy,    'ne'],
          [sx+sw, sy+sh/2,'e'],[sx+sw, sy+sh,  'se'],[sx+sw/2,sy+sh,'s'],
          [sx,    sy+sh, 'sw'],[sx,    sy+sh/2, 'w'],
        ];
        for (const [hx,hy,hname] of handles) {
          if (Math.abs(mx-hx) < 7 && Math.abs(my-hy) < 7) {
            this.selResizeHandle = hname;
            this.selResizeOrigin = { ...sel };
            this.beginSelectionTransform(sel);
            this.isSelTransforming = true;
            if (hname !== 'rot') this.setCursor(this.getResizeCursor(hname as 'nw'|'n'|'ne'|'e'|'se'|'s'|'sw'|'w'));
            return;
          }
        }
      }

      const cell = this.getCellUnbounded(e.clientX, e.clientY);
      if (sel && cell.x >= sel.c1 && cell.x <= sel.c2 && cell.y >= sel.r1 && cell.y <= sel.r2) {
        // Inside selection → start implicit move
        if (this.frame) {
          this.moveStart = cell;
          this.moveSelOrigin = { ...sel };
          this.beginSelectionTransform(sel);
          this.isMoveActive = true;
          this.setCursor('grabbing');
        }
      } else {
        // Outside → start new selection
        const c2 = this.getCell(e.clientX, e.clientY);
        if (c2) { this.isSelecting = true; this.selection = { r1: c2.y, c1: c2.x, r2: c2.y, c2: c2.x }; this.selStart = c2; }
      }
      return;
    }

    // Move tool - begin drag
    if (this.tool === 'move') {
      const cell = this.getCellUnbounded(e.clientX, e.clientY);
      const sel = this.normSel();
      if (sel && this.frame) {
        this.moveStart = cell;
        this.moveSelOrigin = { ...sel };
        this.beginSelectionTransform(sel);
        this.isMoveActive = true;
        this.setCursor('grabbing');
      }
      return;
    }

    // Polygon: click to add points, dblclick to close
    if (this.tool === 'polygon') {
      const cell = this.getCell(e.clientX, e.clientY);
      if (cell) { this.polyPoints.push(cell); this.draw(); }
      return;
    }

    // Other shape tools: drag to draw
    if (['rect','circle','ellipse','triangle','line'].includes(this.tool)) {
      const cell = this.getCell(e.clientX, e.clientY);
      if (cell) { this.shapeStart = cell; this.shapeCurrent = cell; this.isShaping = true; }
      return;
    }

    // Snapshot BEFORE first paint of this stroke - this is the undo target.
    // Fill / pick / select handle their own snapshotting inside the reducer,
    // so dispatching beginStroke for them would create a phantom extra undo step.
    const needsBeginStroke = this.tool !== 'fill' && this.tool !== 'pick';
    if (this.frameId && needsBeginStroke) this.store.dispatch(FrameActions.beginStroke());
    this.isDrawing = true;
    this.didDraw = false;
    this.lastPaintCell = null;
    this.applyPaint(e.clientX, e.clientY);
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(e: MouseEvent): void {
    this._lastPointerClient = { x: e.clientX, y: e.clientY };
    // Broadcast cursor position to collab peers - only when the cell changes to avoid flooding
    const cell = this.getCell(e.clientX, e.clientY);
    const hoverChanged = (!!cell && (!this.hoverCell || this.hoverCell.x !== cell.x || this.hoverCell.y !== cell.y))
      || (!cell && !!this.hoverCell);
    this.hoverCell = cell;
    if (cell) {
      const last = this._lastCursorCell;
      if (!last || last.x !== cell.x || last.y !== cell.y) {
        this._lastCursorCell = cell;
        this.collabSvc.updateLocalCursor(cell.x, cell.y, this.tool);
      }
    }

    // Update cursor when hovering over selection handles (not while floating clip is active)
    if (this.tool === 'select' && this.selection && !this.floatingClip && !this.isSelTransforming && !this.isMoveActive) {
      this.updateSelectionCursor(e);
    }
    if (this.isMidPanning || this.isSpacePanning) { this.ox = e.clientX - this.panStartX; this.oy = e.clientY - this.panStartY; this.draw(); return; }

    // ── Image import: move ghost with drag ──
    if (this.importDragging && this.importDragStart && this.importConfig) {
      const cell = this.getCellUnbounded(e.clientX, e.clientY);
      const newX = Math.round(cell.x - this.importDragStart.x);
      const newY = Math.round(cell.y - this.importDragStart.y);
      this.importX = newX; this.importY = newY;
      this.importConfig = { ...this.importConfig, x: newX, y: newY };
      this.draw();
      return;
    }

    // Drag floating clipboard
    if (this.floatingClip?.isDragging) {
      this.setCursor('grabbing');
      const cell = this.getCellUnbounded(e.clientX, e.clientY);
      this.floatingClip = { ...this.floatingClip, x: cell.x - this.floatingClip.dragOffsetX, y: cell.y - this.floatingClip.dragOffsetY };
      this.draw(); return;
    }

    // Resize floating clipboard
    if (this.isFcResizing && this.fcResizeHandle && this.fcResizeStartBounds && this.fcResizeOrigPixels && this.floatingClip && (e.buttons & 1)) {
      this.setCursor(this.getResizeCursor(this.fcResizeHandle));
      const cell = this.getCellUnbounded(e.clientX, e.clientY);
      const orig = this.fcResizeStartBounds;
      let { r1, c1, r2, c2 } = { ...orig };
      const h = this.fcResizeHandle;
      if (h==='n'||h==='nw'||h==='ne') r1 = Math.min(cell.y, orig.r2);
      if (h==='s'||h==='sw'||h==='se') r2 = Math.max(cell.y, orig.r1);
      if (h==='w'||h==='nw'||h==='sw') c1 = Math.min(cell.x, orig.c2);
      if (h==='e'||h==='ne'||h==='se') c2 = Math.max(cell.x, orig.c1);
      const newW = Math.max(1, c2 - c1 + 1), newH = Math.max(1, r2 - r1 + 1);
      const fc = this.floatingClip;
      if (fc.shapeType) {
        // Re-render the shape at the new size for a crisp outline at any scale
        const result = this.generateShapeClipPixels(fc.shapeType, fc.shapeFillMode!, fc.shapeColor!, newW, newH);
        this.floatingClip = { ...fc, pixels: result.pixels, x: c1, y: r1 };
      } else {
        this.floatingClip = { ...fc, pixels: this.scalePixelsNearest(this.fcResizeOrigPixels, newW, newH), x: c1, y: r1 };
      }
      this.draw(); return;
    }

    if (this.floatingClip && !e.buttons) {
      this.updateFloatingClipHoverCursor(e.clientX, e.clientY);
    }

    if (this.isSelecting && this.selStart) {
      const cell = this.getCell(e.clientX, e.clientY);
      if (cell && this.selection) { this.selection = { r1: this.selStart.y, c1: this.selStart.x, r2: cell.y, c2: cell.x }; this.draw(); }
      return;
    }

    // Selection resize handle drag
    if (this.isSelTransforming && this.selResizeOrigin && this.selResizeHandle && this.frameId && (e.buttons & 1)) {
      if (this.selResizeHandle !== 'rot') this.setCursor(this.getResizeCursor(this.selResizeHandle));
      const cell = this.getCellUnbounded(e.clientX, e.clientY);
      const orig = this.selResizeOrigin;
      let { r1,c1,r2,c2 } = { ...orig };
      const h = this.selResizeHandle;
      if (h==='n'||h==='nw'||h==='ne') r1 = Math.min(cell.y, r2);
      if (h==='s'||h==='sw'||h==='se') r2 = Math.max(cell.y, r1);
      if (h==='w'||h==='nw'||h==='sw') c1 = Math.min(cell.x, c2);
      if (h==='e'||h==='ne'||h==='se') c2 = Math.max(cell.x, c1);
      this.selection = { r1, c1, r2, c2 };

      // Default (no Shift): stretch-scale the captured pixels into new bounds live
      if (!e.shiftKey && this.transformSourcePixels) {
        const dstW = c2 - c1 + 1, dstH = r2 - r1 + 1;
        const scaled = this.scalePixelsNearest(this.transformSourcePixels, dstW, dstH);
        this.transformPreviewPixels = scaled;
        this.transformPreviewRow = r1;
        this.transformPreviewCol = c1;
      }
      this.draw(); return;
    }

    // Rotation drag
    if (this.isSelTransforming && this.selRotCenter && this.frameId && this.transformOriginSel && this.transformSourcePixels && (e.buttons & 1)) {
      this.setCursor('crosshair');
      const canvas = this.canvasRef.nativeElement;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      const angle = Math.atan2(my - this.selRotCenter.y, mx - this.selRotCenter.x) * 180 / Math.PI + 90;
      this.selRotAngle = angle;
      const rotated = this.rotatePixelsResampled(this.transformSourcePixels, this.selRotAngle);
      const origin = this.transformOriginSel;
      const srcW = this.transformSourcePixels[0]?.length ?? 0;
      const srcH = this.transformSourcePixels.length;
      const outW = rotated[0]?.length ?? 0;
      const outH = rotated.length;
      const centerX = origin.c1 + (srcW - 1) / 2;
      const centerY = origin.r1 + (srcH - 1) / 2;
      const rawDestCol = Math.round(centerX - (outW - 1) / 2);
      const rawDestRow = Math.round(centerY - (outH - 1) / 2);
      const trimmed = this.trimTransparentPadding(rotated);
      const destCol = rawDestCol + trimmed.colOffset;
      const destRow = rawDestRow + trimmed.rowOffset;
      this.transformPreviewPixels = trimmed.pixels;
      this.transformPreviewRow = destRow;
      this.transformPreviewCol = destCol;
      const trimmedW = trimmed.pixels[0]?.length ?? 0;
      const trimmedH = trimmed.pixels.length;
      this.selection = {
        r1: destRow,
        c1: destCol,
        r2: destRow + Math.max(0, trimmedH - 1),
        c2: destCol + Math.max(0, trimmedW - 1)
      };
      this.draw(); return;
    }

    // Move tool drag
    if (this.isMoveActive && this.moveStart && this.moveSelOrigin && this.transformSourcePixels && this.frameId && (e.buttons & 1)) {
      this.setCursor('grabbing');
      const cell = this.getCellUnbounded(e.clientX, e.clientY);
      const dr = cell.y - this.moveStart.y, dc = cell.x - this.moveStart.x;
      const orig = this.moveSelOrigin;
      this.transformPreviewPixels = this.transformSourcePixels;
      this.transformPreviewRow = orig.r1 + dr;
      this.transformPreviewCol = orig.c1 + dc;
      // Update selection to follow
      this.selection = { r1: orig.r1+dr, c1: orig.c1+dc, r2: orig.r2+dr, c2: orig.c2+dc };
      this.draw(); return;
    }

    // Shape preview
    if (this.isShaping) {
      const cell = this.getCell(e.clientX, e.clientY);
      if (cell) { this.shapeCurrent = cell; this.draw(); }
      return;
    }

    if (this.isDrawing && (e.buttons & 1)) this.applyPaint(e.clientX, e.clientY);

    // Select-tool handle hover - skip when a floating clip is active (its cursors were set above)
    if (!e.buttons && !this.floatingClip) this.updateCursorForPosition(e.clientX, e.clientY);
    if (!e.buttons && hoverChanged && (this.tool === 'draw' || this.tool === 'erase')) this.draw();
  }

  @HostListener('mouseup', ['$event'])
  onMouseUp(e: MouseEvent): void {
    if (e.button === 1) { this.isMidPanning = false; this.isPanning = this.isSpacePanning; }
    if (e.button === 0) {
      if (this.floatingClip?.isDragging) { this.floatingClip = { ...this.floatingClip, isDragging: false }; }
      if (this.isFcResizing) { this.isFcResizing = false; this.fcResizeHandle = null; this.fcResizeStartBounds = null; this.fcResizeOrigPixels = null; }
      const wasPainting = this.isDrawing;
      this.isDrawing = false; this.didDraw = false; this.lastPaintCell = null; this.isSelecting = false; this.selStart = null;
      // Broadcast after paint stroke completes
      if (wasPainting && this.frameId && this.frame) {

      }
      if (this.isMoveActive) {
        if (this.frameId && this.transformOriginSel && this.transformPreviewPixels) {
          this.store.dispatch(FrameActions.applySelectionTransform({
            frameId: this.frameId,
            origin: this.transformOriginSel,
            pixels: this.transformPreviewPixels,
            destRow: this.transformPreviewRow,
            destCol: this.transformPreviewCol
          }));
        }
        this.isMoveActive = false;
        this.moveStart = null;
        this.moveSelOrigin = null;
      }
      if (this.isSelTransforming) {
        if (this.frameId && this.transformOriginSel && this.transformPreviewPixels) {
          this.store.dispatch(FrameActions.applySelectionTransform({
            frameId: this.frameId,
            origin: this.transformOriginSel,
            pixels: this.transformPreviewPixels,
            destRow: this.transformPreviewRow,
            destCol: this.transformPreviewCol
          }));
        }
        this.selRotAngle = 0;
        this.isSelTransforming = false;
        this.selResizeHandle = null; this.selResizeOrigin = null;
        this.selRotCenter = null; this.selRotPixels = null;
        this.draw();
      }
      this.transformSourcePixels = null;
      this.transformOriginSel = null;
      this.transformPreviewPixels = null;
      if (this.floatingClip) this.updateFloatingClipHoverCursor(e.clientX, e.clientY);
      else this.updateCursorForPosition(e.clientX, e.clientY);
      // Commit shape (with symmetry)
      if (this.isShaping && this.shapeStart && this.shapeCurrent && this.frameId) {
        const basePts = this.getShapePixels(this.shapeStart, this.shapeCurrent);
        const { width, height } = this.settings!.Resolution;
        // Collect all pixels after symmetry
        const allPts = new Set<string>();
        for (const p of basePts) {
          for (const sp of this.symmetryPoints(p)) {
            allPts.add(`${sp.x},${sp.y}`);
          }
        }
        // All shapes (including line) become a floating clip so the user can drag to reposition before placing
        let minC = Infinity, maxC = -Infinity, minR = Infinity, maxR = -Infinity;
        allPts.forEach(k => {
          const [x, y] = k.split(',').map(Number);
          if (y >= 0 && y < height && x >= 0 && x < width) {
            if (x < minC) minC = x; if (x > maxC) maxC = x;
            if (y < minR) minR = y; if (y > maxR) maxR = y;
          }
        });
        if (minC !== Infinity) {
          const clipH = maxR - minR + 1;
          const clipW = maxC - minC + 1;
          const clipPixels: string[][] = Array.from({ length: clipH }, () => Array(clipW).fill('#00000000'));
          allPts.forEach(k => {
            const [x, y] = k.split(',').map(Number);
            if (y >= 0 && y < height && x >= 0 && x < width) {
              clipPixels[y - minR][x - minC] = this.color;
            }
          });
          this.floatingClip = {
            pixels: clipPixels, x: minC, y: minR,
            isDragging: false, dragOffsetX: 0, dragOffsetY: 0,
            shapeType: this.tool as ToolType,
            shapeFillMode: this.shapeFillMode,
            shapeColor: this.color,
          };
        }
        this.isShaping = false; this.shapeStart = null; this.shapeCurrent = null; this.shapeDimsVisible = false; this.draw();
      }
    }
  }

  @HostListener('dblclick', ['$event'])
  onDblClick(e: MouseEvent): void {
    if (this.isReadOnlyInRoom()) return;
    if (this.tool === 'polygon' && this.polyPoints.length >= 3 && this.frameId && this.frame && this.settings) {
      const { width, height } = this.settings.Resolution;
      const newPixels = this.frame.pixels.map(r => [...r]);
      // Draw lines between consecutive points and close the polygon
      const pts = [...this.polyPoints, this.polyPoints[0]];
      for (let i = 0; i < pts.length - 1; i++) {
        const line = this.bresenhamLine(pts[i], pts[i + 1]);
        for (const p of line) {
          for (const sp of this.symmetryPoints(p)) {
            const { x, y } = sp;
            if (y >= 0 && y < height && x >= 0 && x < width) newPixels[y][x] = this.color;
          }
        }
      }
      this.store.dispatch(FrameActions.replaceFramePixels({ frameId: this.frameId, pixels: newPixels }));
      this.polyPoints = [];
      this.draw();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.isDrawing = false;
    this.hoverCell = null;
    this.draw();
    this.setCursor(this.getToolCursor());
  }
  @HostListener('contextmenu', ['$event']) onContextMenu(e: MouseEvent): void { e.preventDefault(); }

  @HostListener('wheel', ['$event'])
  onWheel(e: WheelEvent): void {
    e.preventDefault();
    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    const mx = e.clientX - rect.left, my = e.clientY - rect.top;
    const factor = e.deltaY > 0 ? 0.88 : 1.12;
    const nz = Math.max(0.1, Math.min(20, this.zoom * factor));
    this.ox = mx - (mx - this.ox) * (nz / this.zoom);
    this.oy = my - (my - this.oy) * (nz / this.zoom);
    this.zoom = nz; this.draw();
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(e: KeyboardEvent): void {
    const tag      = (e.target as HTMLElement)?.tagName?.toLowerCase();
    const inputType = ((e.target as HTMLInputElement)?.type ?? '').toLowerCase();
    // Allow undo/redo through even when a non-text input has focus (range sliders,
    // checkboxes, selects in the sidebar).  Only real text fields should block it.
    const isTextInput = tag === 'textarea'
      || (tag === 'input' && inputType !== 'range' && inputType !== 'checkbox' && inputType !== 'radio');
    const hasMod = e.ctrlKey || e.metaKey;
    if (hasMod && !isTextInput) {
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) { e.preventDefault(); this.store.dispatch(this.collabSvc.isInRoom$.value ? FrameActions.undoCollab() : FrameActions.undo()); return; }
      if (key === 'y' || (key === 'z' && e.shiftKey)) { e.preventDefault(); this.store.dispatch(this.collabSvc.isInRoom$.value ? FrameActions.redoCollab() : FrameActions.redo()); return; }
    }
    // Place floating shape / paste preview (before isTextInput return: canvas has no focus as an input)
    if (e.key === 'Enter' && !e.repeat && this.floatingClip && !this.isReadOnlyInRoom()) {
      const t = e.target as HTMLElement;
      const inFloatingHint = !!t.closest?.('.floating-hint');
      if (inFloatingHint || !isTextInput) {
        e.preventDefault();
        this.stampFloatingClip();
        return;
      }
    }
    if (isTextInput || tag === 'select') return;

    if (e.code === 'Space') {
      e.preventDefault();
      if (!this.isSpacePanning) {
        this.isSpacePanning = true;
        this.isPanning = true;
        // Anchor to current mouse position so panning starts immediately on mousemove
        const p = this._lastPointerClient;
        this.panStartX = (p?.x ?? 0) - this.ox;
        this.panStartY = (p?.y ?? 0) - this.oy;
      }
    }
    if (e.key === 'f' || e.key === 'F') { this.centered = false; this.centerCanvas(); this.draw(); }
    if (this.isReadOnlyInRoom()) return;

    // Handle modified shortcuts (undo/redo already handled above for sidebar inputs).
    if (hasMod) {
      const key = e.key.toLowerCase();
      if (key === 'a') { e.preventDefault(); this.selectAll(); return; }
      if (key === 'c') { e.preventDefault(); this.copySelection(); return; }
      if (key === 'x') { e.preventDefault(); this.cutSelection(); return; }
      if (key === 'v') { e.preventDefault(); if (this.floatingClip) this.stampFloatingClip(); return; }
      if (key === 'z' && !e.shiftKey) { e.preventDefault(); this.store.dispatch(this.collabSvc.isInRoom$.value ? FrameActions.undoCollab() : FrameActions.undo()); return; }
      if (key === 'y' || (key === 'z' && e.shiftKey)) { e.preventDefault(); this.store.dispatch(this.collabSvc.isInRoom$.value ? FrameActions.redoCollab() : FrameActions.redo()); return; }
      if (key === ']') { this.store.dispatch(ToolActions.setBrushSize({ size: Math.min(16, this.brushSize + 1) })); return; }
      if (key === '[') { this.store.dispatch(ToolActions.setBrushSize({ size: Math.max(1, this.brushSize - 1) })); return; }
      return;
    }

    if (e.key === 'b' || e.key === 'B') this.store.dispatch(ToolActions.selectTool({ tool: 'draw' }));
    if (e.key === 'e' || e.key === 'E') this.store.dispatch(ToolActions.selectTool({ tool: 'erase' }));
    if (e.key === 'g' || e.key === 'G') this.store.dispatch(ToolActions.selectTool({ tool: 'fill' }));
    if (e.key === 'i' || e.key === 'I') this.store.dispatch(ToolActions.selectTool({ tool: 'pick' }));
    if (e.key === 's' || e.key === 'S') this.store.dispatch(ToolActions.selectTool({ tool: 'select' }));
    if (e.key === 'm' || e.key === 'M') this.store.dispatch(ToolActions.selectTool({ tool: 'move' }));
    // Add blank frame shortcut (respects collab permissions)
    if (e.key === 'a' || e.key === 'A') {
      if (this.collabSvc.can('createFrame')) {
        this.store.dispatch(FrameActions.addBlankFrame());
      }
    }
    if ((e.key === 'r' || e.key === 'R') && this.selection) {
      e.preventDefault(); this.rotateSelection90(e.shiftKey ? false : true);
    }
    if (e.key === 'Escape') { if (this.floatingClip) this.stampFloatingClip(); else this.deselect(); this.polyPoints = []; }
    if ((e.key === 'Delete' || e.key === 'Backspace') && this.selection && !this.floatingClip) {
      e.preventDefault(); this.clearSelectionArea();
    }

    // Arrow keys: move floating clip OR nudge selection pixels
    if (e.key.startsWith('Arrow')) {
      const d = e.shiftKey ? 5 : 1;
      if (this.floatingClip) {
        e.preventDefault();
        const dx = e.key === 'ArrowLeft' ? -d : e.key === 'ArrowRight' ? d : 0;
        const dy = e.key === 'ArrowUp'   ? -d : e.key === 'ArrowDown'  ? d : 0;
        this.floatingClip = { ...this.floatingClip, x: this.floatingClip.x + dx, y: this.floatingClip.y + dy };
        this.draw();
      } else if (this.selection && this.frame && this.frameId) {
        e.preventDefault();
        const dx = e.key === 'ArrowLeft' ? -d : e.key === 'ArrowRight' ? d : 0;
        const dy = e.key === 'ArrowUp'   ? -d : e.key === 'ArrowDown'  ? d : 0;
        const sel = this.normSel()!;
        const { height, width } = this.settings!.Resolution;
        const extracted = this.extractPixelsFromRect(sel, this.frame.pixels);
        const newPixels = this.frame.pixels.map(r => [...r]);
        for (let r = sel.r1; r <= sel.r2; r++) for (let c = sel.c1; c <= sel.c2; c++) newPixels[r][c] = '#00000000';
        for (let r = 0; r < extracted.length; r++) for (let c = 0; c < extracted[r].length; c++) {
          const nr = sel.r1 + dy + r, nc = sel.c1 + dx + c;
          if (nr >= 0 && nr < height && nc >= 0 && nc < width) newPixels[nr][nc] = extracted[r][c];
        }
        this.store.dispatch(FrameActions.replaceFramePixels({ frameId: this.frameId, pixels: newPixels }));
        this.selection = { r1: sel.r1+dy, c1: sel.c1+dx, r2: sel.r2+dy, c2: sel.c2+dx };
        this.draw();
      }
    }
  }

  @HostListener('document:keyup', ['$event'])
  onKeyUp(e: KeyboardEvent): void { if (e.code === 'Space') { this.isSpacePanning = false; this.isPanning = this.isMidPanning; } }

  @HostListener('document:mouseup')
  onDocMouseUp(): void {
    if ((this.isMoveActive || this.isSelTransforming) && this.frameId && this.transformOriginSel && this.transformPreviewPixels) {
      this.store.dispatch(FrameActions.applySelectionTransform({
        frameId: this.frameId,
        origin: this.transformOriginSel,
        pixels: this.transformPreviewPixels,
        destRow: this.transformPreviewRow,
        destCol: this.transformPreviewCol
      }));
    }
    this.isMidPanning = false;
    this.isSpacePanning = false;
    this.isPanning = false;
    this.isDrawing = false;
    this.isSelecting = false;
    this.isMoveActive = false;
    this.isSelTransforming = false;
    this.selResizeHandle = null;
    this.selResizeOrigin = null;
    this.selRotCenter = null;
    this.selRotAngle = 0;
    this.moveStart = null;
    this.moveSelOrigin = null;
    this.transformSourcePixels = null;
    this.transformOriginSel = null;
    this.transformPreviewPixels = null;
    this.importDragging = false;
    this.importDragStart = null;
    if (this._lastPointerClient) this.updateCursorForPosition(this._lastPointerClient.x, this._lastPointerClient.y);
    else this.setCursor(this.getToolCursor());
  }

}

